import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// --- Sub-Components ---
import MapContainer from "./MapContainer";
import MapSearch from "./MapSearch";
import MapActionBar from "./MapActionBar";
import DeviceSidebar from "./DeviceSidebar";
import RightInfoPanel from "./RightInfoPanel";
import TripPlayback from "./TripPlayback";
import AlertsPanel from "./AlertsPanel";
// --- Modals ---
import AddTripModal from "../modals/AddTripModal";
import AddLicenseModal from "../modals/AddLicenseModal";
import AddExpenseModal from "../modals/AddExpenseModal";
import AddDriverModal from "../modals/AddDriverModal";
import AddDocumentModal from "../modals/AddDocumentModal";
import AddMaterialModal from "../modals/AddMaterialModal";
import AddClientModal from "../modals/AddClientModal";
import AddRouteModal from "../modals/AddRouteModal";
import AddSensorModal from "../modals/AddSensorModal";
import AddReminderModal from "../modals/AddReminderModal";
import GeofenceModal from "../admin/GeofenceModal";
import SharedGeofenceModal from "./SharedGeofenceModal";
import GeofenceAssignModal from "./GeofenceAssignModal";
import LandmarkAssignModal from "./LandmarkAssignModal";
import ReportsPortal from "../reports/ReportsPortal";
import HistoryPlayback from "./HistoryPlayback";
import RegisterDeviceModal from "./RegisterDeviceModal";
import MapCameraPopup from "./livecamera/MapCameraPopup";

import "./HistoryPlayback.css";
import { History } from "lucide-react";
import "./TripHistoryPanel.css";
import "./TripPlayback.css";
// --- API Hooks ---
import {
  useGetTripsQuery,
  useGetRoutesQuery,
  useAddTripMutation,
  useStartTripMutation,
  useCompleteTripMutation,
  useAddExpenseMutation,
  useAddRouteMutation,
  useAddSensorMutation,
  useAddLicenseMutation,
  useAddDocumentMutation,
  useAddMaterialMutation,
  useAddReminderMutation,
  useAddClientMutation,
} from "../../redux/logistics/logisticsApi";
import InMapHistoryOverlay from "./InMapHistoryOverlay";

import {
  useCreateUserMutation,
  useCreateSharedGeofenceMutation,
  useCreateLandmarkMutation,
  useDeleteSharedGeofenceMutation,
  useDeleteLandmarkMutation,
  useEditDeviceMutation,
  useGetSharedGeofencesQuery,
  useGetLandmarksQuery,
  useAssignLandmarkDeviceMutation,
  useUpdateSharedGeofenceMutation,
  useUpdateLandmarkMutation,
  useAssignSharedGeofenceDeviceMutation,
  useUnassignLandmarkDeviceMutation,
  useUnassignSharedGeofenceDeviceMutation,
  useLazyGetViewportVehiclesQuery,
} from "../../redux/admin/adminApi";

import { ROLES, hasUserPermission } from "../../utils/rbacHelper";
import { silverMapStyle, fleetMapStyles } from "./MapStyles";
import "./advanced-map.css";
import { useStatus } from "../../popup/NotificationContext";
import MapLayerPanel from "./MapLayerPanel";
import TripHistoryPanel from "./TripHistoryPanel";
import {
  useGetAlarmsQuery,
  useGetDeviceAlarmHistoryQuery,
} from "../../redux/admin/alarmApi";
import { useSocket } from "../../socket/SocketContext";
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES } from "../../config/googleMaps";
import { getDeviceStatus, isValidGPS } from "../../utils/vehicleStatus";
import { beginProfile, markRenderTrigger, profileCallback } from "../../utils/renderProfiler";
import { useQuickModal } from "../../hooks/QuickModalContext";
import { useDevRenderInstrumentation } from "../../utils/devInstrumentation";
import { LIVE_MAP_PATH } from "../../router/dashboardNavigation";

const FOLLOW_SELECTION_MIN_ZOOM = 16;
const NAVBAR_OFFSET = 64;
const WEB_MAP_ICON_CAP = 5000;
const INDIA_MAP_CENTER = { lat: 22.5937, lng: 78.9629 };
const INDIA_OVERVIEW_ZOOM = 5;

const getRecencyTime = (item) => {
  const updatedAt = new Date(item?.updatedAt || item?.createdAt || 0).getTime();
  return Number.isFinite(updatedAt) ? updatedAt : 0;
};

// ================= UTILITY FUNCTIONS =================
const isLiveGps = (device) => {
  const ld = device?.liveData;
  if (!ld) return false;

  if (ld.isStale === true) return false;
  if (ld.fixStatus === "no_fix") return false;
  if (typeof ld.gpsDelaySeconds === "number" && ld.gpsDelaySeconds > 60)
    return false;
  if (ld.statusFlags && ld.statusFlags.isGpsFixed === false) return false;

  const dataAge = ld.timestamp
    ? Date.now() - new Date(ld.timestamp).getTime()
    : Infinity;
  if (dataAge > 30000) return false;

  return true;
};

const getSocketMapLocation = (socketData = {}) => {
  if (!socketData) {
    return { latitude: null, longitude: null };
  }

  const latitude = socketData?.latitude ?? socketData?.lat ?? null;
  const longitude = socketData?.longitude ?? socketData?.lng ?? null;
  if (!isValidGPS(Number(latitude), Number(longitude))) {
    return { latitude: null, longitude: null };
  }

  return {
    latitude,
    longitude,
  };
};

function AdvancedMapTracker({
  devicesOnMap = [],
  sidebarDevices = [],
  deviceCounts,
  totalDeviceCount = 0,
  progressiveLoading,
  deviceAddresses,
  getDirections,
  handleOpenCommandModal,
  getVehicleIcon,
  routeStart,
  routeEnd,
  routeDetails,
  setRouteDetails,
  deviceEvents = [],
  onStartTrip,
  onEndTrip,
  activeTrips = [],
  onDismissAlert,
  currentUserRole,
  onExportData,
  onPrintView,
  quickAction,
  clearQuickAction,
  externalRouteFocus,
  clearExternalRouteFocus,
  externalPlaybackFocus,
  clearExternalPlaybackFocus,
  initialSidebarStatus = "all",
  onMapViewportChange,
}) {
  const finishMapTrackerProfile = beginProfile("MapTracker", { stage: "component" });
  const mapTrackerProfileRef = useRef(null);
  const theme = useTheme();
  const layoutRef = useRef(null);
  const location = useLocation();
  const isCurrentMapRoute =
    location.pathname === LIVE_MAP_PATH ||
    (location.pathname === "/dashboard" &&
      new URLSearchParams(location.search).get("tab") === "map");
  console.debug("[MAP ROUTE]", location.pathname, location.search, {
    activeTab: "map",
    isCurrentMapRoute,
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactDesktopWidth = useMediaQuery("(max-width: 1536px)");
  const isCompactDesktopHeight = useMediaQuery("(max-height: 900px)");
  const isCompactDesktop = !isMobile && (isCompactDesktopWidth || isCompactDesktopHeight);
  const currentUser = useSelector((state) => state.auth.user);
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const { showStatus } = useStatus();
  const isHR = currentUserRole === ROLES.HR;
  const isViewer = currentUserRole === ROLES.VIEWER;

  // ================= STATE =================
  const [map, setMap] = useState(null);
  // Checkbox selection controls which loaded vehicles are visible. Focus is
  // separate so clicking one vehicle after Select All only highlights it.
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [focusedDeviceId, setFocusedDeviceId] = useState("");
  const [sidebarFocusedDeviceId, setSidebarFocusedDeviceId] = useState("");
  const [sidebarLoadedDevices, setSidebarLoadedDevices] = useState([]);
  useDevRenderInstrumentation("MapTracker", () => ({
    devicesOnMapCount: Array.isArray(devicesOnMap) ? devicesOnMap.length : 0,
    sidebarDevicesCount: Array.isArray(sidebarDevices) ? sidebarDevices.length : 0,
    selectedDevicesCount: selectedDevices.length,
    totalDeviceCount,
    initialSidebarStatus,
    isMobile,
  }));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const [showBicycle, setShowBicycle] = useState(false);
  const [showTolls, setShowTolls] = useState(false);
  const [mapType, setMapType] = useState("roadmap");
  const [followMode, setFollowMode] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [showPOI, setShowPOI] = useState(true);
  const [showLayerPanel, setShowLayerPanel] = useState(false);
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [placesViewActive, setPlacesViewActive] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedImei, setSelectedImei] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mapItems, setMapItems] = useState([]);
const [mapMode, setMapMode] = useState("cluster");
const [mapTotal, setMapTotal] = useState(0);
const [mapLoading, setMapLoading] = useState(false);
const [mapError, setMapError] = useState(null);
const mapRequestSeqRef = useRef(0);
const mapFetchTimerRef = useRef(null);
const autoFollowSelectionKeyRef = useRef("");

const [loadViewportVehicles] = useLazyGetViewportVehiclesQuery();
  const [mobilePanelTab, setMobilePanelTab] = useState("cars");
  const [isBottomExpanded, setIsBottomExpanded] = useState(true);
  const [bookmarks, setBookmarks] = useState([]); // ✅ ADDED

  useEffect(() => {
    const handleFullscreenChange = () => {
      const activeFullscreenElement = document.fullscreenElement;
      setFullscreen(activeFullscreenElement === layoutRef.current);

      if (map && window.google?.maps?.event) {
        window.setTimeout(() => {
          window.google.maps.event.trigger(map, "resize");
        }, 120);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [map]);

  mapTrackerProfileRef.current = {
    props: {
      devicesOnMapCount: devicesOnMap.length,
      sidebarDevicesCount: sidebarDevices.length,
      selectedDevicesCount: selectedDevices.length,
      totalDeviceCount,
    },
    state: {
      mapMode,
      mapItemsCount: mapItems.length,
      mapLoading,
      searchTerm,
      followMode,
    },
    deviceCount: devicesOnMap.length,
  };


  // Vehicle visibility filters
  const [showIdleVehicles, setShowIdleVehicles] = useState(true);
  const [showMovingVehicles, setShowMovingVehicles] = useState(true);
  const [showStoppedVehicles, setShowStoppedVehicles] = useState(true);
  const [showInactiveVehicles, setShowInactiveVehicles] = useState(true); // ✅ Changed from offline

  useEffect(() => {
    if (mapTrackerProfileRef.current) {
      finishMapTrackerProfile(mapTrackerProfileRef.current);
    }
  });

  useEffect(() => {
    if (isCurrentMapRoute) return undefined;
    if (mapFetchTimerRef.current) {
      clearTimeout(mapFetchTimerRef.current);
      mapFetchTimerRef.current = null;
    }
    mapRequestSeqRef.current += 1;
    setMapItems([]);
    setMapLoading(false);
    setSelectedDevice(null);
    setSelectedImei(null);
    return undefined;
  }, [isCurrentMapRoute]);

  // History & Playback
  const [showHistory, setShowHistory] = useState(false);
  const [showTripHistory, setShowTripHistory] = useState(false);
  const [historyDevice, setHistoryDevice] = useState(null);
  const [showInMapHistory, setShowInMapHistory] = useState(false);
  const [sidebarHistorySelection, setSidebarHistorySelection] = useState(null);

  // Modals
  const [showPlayback, setShowPlayback] = useState(false);
  const [playbackData, setPlaybackData] = useState(null);
  const [playbackFrameData, setPlaybackFrameData] = useState(null);
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [hiddenLiveMarkers, setHiddenLiveMarkers] = useState(() => new Set());
  const [showAlerts, setShowAlerts] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedDeviceData, setSelectedDeviceData] = useState(null);
  const [panelResolvedAddress, setPanelResolvedAddress] = useState("");
  const [showRegisterDeviceModal, setShowRegisterDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [showCameraPopup, setShowCameraPopup] = useState(false);

  const [showTripModal, setShowTripModal] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showSensorModal, setShowSensorModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [selectedTripForMap, setSelectedTripForMap] = useState(null);
  const [selectedRouteForMap, setSelectedRouteForMap] = useState(null);
  const [selectedRoutesForMap, setSelectedRoutesForMap] = useState([]);

  const [bottomPanelHeight, setBottomPanelHeight] = useState(0);
  const [selectedDeviceForGeofence, setSelectedDeviceForGeofence] =
    useState(null);
  const [geofenceName, setGeofenceName] = useState("");
  const [unsavedGeofence, setUnsavedGeofence] = useState(null);
  const [showReports, setShowReports] = useState(false);
  const [reportDevice, setReportDevice] = useState(null);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [selectedLandmark, setSelectedLandmark] = useState(null);
  const [draftLandmark, setDraftLandmark] = useState(null);
  const [landmarkFocusPoint, setLandmarkFocusPoint] = useState(null);
  const [landmarkPlacementActive, setLandmarkPlacementActive] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [liveAlarmNotifications, setLiveAlarmNotifications] = useState([]);
  const [geofenceSuccessMessage, setGeofenceSuccessMessage] = useState("");
  const [geofenceErrorMessage, setGeofenceErrorMessage] = useState("");
  const [showSharedGeofenceModal, setShowSharedGeofenceModal] = useState(false);
  const [sharedGeofenceShapeType, setSharedGeofenceShapeType] = useState("polygon");
  const [sharedGeofenceMode, setSharedGeofenceMode] = useState("create");
  const [editingSharedGeofence, setEditingSharedGeofence] = useState(null);
  const [showAssignGeofenceModal, setShowAssignGeofenceModal] = useState(false);
  const [assigningGeofence, setAssigningGeofence] = useState(null);
  const [pendingGeofenceDeviceIds, setPendingGeofenceDeviceIds] = useState({});
  const [showAssignLandmarkModal, setShowAssignLandmarkModal] = useState(false);
  const [assigningLandmark, setAssigningLandmark] = useState(null);
  const [pendingLandmarkDeviceIds, setPendingLandmarkDeviceIds] = useState({});

  // ================= REFS =================
  const connectionToastRef = useRef("unknown");
  const mobileAutoCenterRef = useRef(null);
  const subscribedImeisRef = useRef(new Set());
  const realtimeFlushTimerRef = useRef(null);
  const realtimeFlushPendingRef = useRef(false);
  const { openModal } = useQuickModal();



  const normalizeMapVehicleItem = useCallback((item) => {
  if (!item || item.type !== "vehicle") return null;

  return {
    _id: item.id,
    id: item.id,
    imei: item.imei || "",
    name: item.name || item.imei || "Vehicle",
    vehicleNumber: item.vehicleNumber || "",
    vehicleType: item.iconCategory || item.vehicleType || "car",
    deviceIcon: item.iconCategory || "car",
    status: item.status || "no_data",
    vehicleStatus: item.status || item.iconStatus || "no_data",
    liveData: {
      latitude: Number(item.latitude),
      longitude: Number(item.longitude),
      speed: Number(item.speed ?? item.currentSpeed ?? item.speedKmh ?? item.speed_kmh ?? 0),
      heading: Number(item.heading || 0),
      vehicleStatus: item.status || item.iconStatus || "no_data",
      timestamp: item.lastDataReceivedAt || null,
    },
    currentLocation: {
      latitude: Number(item.latitude),
      longitude: Number(item.longitude),
      speed: Number(item.speed ?? item.currentSpeed ?? item.speedKmh ?? item.speed_kmh ?? 0),
      heading: Number(item.heading || 0),
      timestamp: item.lastDataReceivedAt || null,
    },
    latitude: Number(item.latitude),
    longitude: Number(item.longitude),
    lat: Number(item.latitude),
    lng: Number(item.longitude),
    lastDataReceivedAt: item.lastDataReceivedAt || null,
    __mapLite: true,
  };
}, []);



const mapVehicles = useMemo(() => {
  return mapItems
    .filter((item) => item?.type === "vehicle")
    .map(normalizeMapVehicleItem)
    .filter(Boolean)
    .slice(0, WEB_MAP_ICON_CAP);
}, [mapItems, normalizeMapVehicleItem]);

const mapClusters = useMemo(() => {
  return mapItems
    .filter((item) => item?.type === "cluster")
    .slice(0, WEB_MAP_ICON_CAP);
}, [mapItems]);

const visibleMapVehicleImeis = useMemo(() => {
  return mapVehicles
    .map((device) => String(device?.imei || "").trim())
    .filter(Boolean);
}, [mapVehicles]);


const handleMapViewportChange = useCallback((snapshot) => {
  markRenderTrigger("MapTracker.handleMapViewportChange", {
    zoom: Number(snapshot?.zoom || 0),
    hasBounds: Boolean(snapshot?.bounds),
    searchTerm,
  });
  if (!snapshot?.bounds) return;
  if (placesViewActive) return;

  const bounds = snapshot.bounds;
  const zoom = Number(snapshot.zoom || map?.getZoom?.() || 5);

  const north = Number(bounds.north);
  const south = Number(bounds.south);
  const east = Number(bounds.east);
  const west = Number(bounds.west);

  if (![north, south, east, west, zoom].every(Number.isFinite)) return;

  if (mapFetchTimerRef.current) {
    clearTimeout(mapFetchTimerRef.current);
  }

  mapFetchTimerRef.current = setTimeout(async () => {
    await profileCallback("MapTracker.viewportFetch", { zoom, searchTerm }, async () => {
    const seq = mapRequestSeqRef.current + 1;
    mapRequestSeqRef.current = seq;

    setMapLoading(true);
    setMapError(null);

    try {
      const payload = await loadViewportVehicles({
        north,
        south,
        east,
        west,
        zoom,
        filters:
          showMovingVehicles &&
          showIdleVehicles &&
          showStoppedVehicles &&
          showInactiveVehicles
            ? "all"
            : "all",
        search: searchTerm || "",
      }).unwrap();

      if (seq !== mapRequestSeqRef.current) return;

      const items = Array.isArray(payload?.items) ? payload.items : [];

      setMapItems(items);
      setMapMode(payload?.mode || "cluster");
      setMapTotal(Number(payload?.total || items.length || 0));
    } catch (err) {
      if (seq !== mapRequestSeqRef.current) return;

      console.error("[MAP VIEWPORT API ERROR]", err);
      setMapError(err);

      // Safe fallback: old behavior remains usable.
      setMapItems([]);
      setMapMode("fallback");
    } finally {
      if (seq === mapRequestSeqRef.current) {
        setMapLoading(false);
      }
    }
    });
  }, 500);
}, [
  loadViewportVehicles,
  map,
  placesViewActive,
  searchTerm,
  showMovingVehicles,
  showIdleVehicles,
  showStoppedVehicles,
  showInactiveVehicles,
]);


useEffect(() => {
  if (!map) return undefined;

  const timer = setTimeout(() => {
    const bounds = map.getBounds?.();
    if (!bounds) return;

    const ne = bounds.getNorthEast?.();
    const sw = bounds.getSouthWest?.();
    if (!ne || !sw) return;

    handleMapViewportChange({
      zoom: map.getZoom?.() || 5,
      bounds: {
        north: ne.lat(),
        east: ne.lng(),
        south: sw.lat(),
        west: sw.lng(),
      },
    });
  }, 350);

  return () => clearTimeout(timer);
}, [
  map,
  searchTerm,
  showMovingVehicles,
  showIdleVehicles,
  showStoppedVehicles,
  showInactiveVehicles,
  handleMapViewportChange,
]);




useEffect(() => {
  return () => {
    if (mapFetchTimerRef.current) {
      clearTimeout(mapFetchTimerRef.current);
    }
  };
}, []);

  // ================= API HOOKS =================
  const {
    connected,
    on,
    socketUpdatesRef,
    subscribeToDevice,
    subscribeToDevices,
    unsubscribeFromDevice,
    unsubscribeFromDevices,
  } = useSocket();
  const {
    data: alarms = [],
    refetch: refetchAlarms,
    isFetching: alarmsRefreshing,
  } = useGetAlarmsQuery({
    limit: 150,
    status: "active",
  });
  const {
    data: selectedDeviceAlarmHistory = [],
    refetch: refetchSelectedDeviceAlarmHistory,
    isFetching: selectedDeviceAlarmHistoryRefreshing,
  } = useGetDeviceAlarmHistoryQuery(
    { deviceId: selectedDevice?._id, limit: 150 },
    { skip: !selectedDevice?._id }
  );
  const { data: sharedGeofences = [] } = useGetSharedGeofencesQuery();
  const { data: landmarks = [] } = useGetLandmarksQuery();

  const [addTrip] = useAddTripMutation();
  const [startTripMutation] = useStartTripMutation();
  const [completeTripMutation] = useCompleteTripMutation();
  const [addExpense] = useAddExpenseMutation();
  const [addRoute] = useAddRouteMutation();
  const [addSensor] = useAddSensorMutation();
  const [addMaterial] = useAddMaterialMutation();
  const [addLicense] = useAddLicenseMutation();
  const [addDocument] = useAddDocumentMutation();
  const [addReminder] = useAddReminderMutation();
  const [createUser] = useCreateUserMutation();
  const [addClient] = useAddClientMutation();
  const [editDevice] = useEditDeviceMutation();
  const [createSharedGeofence, { isLoading: creatingSharedGeofence }] = useCreateSharedGeofenceMutation();
  const [updateSharedGeofence, { isLoading: updatingSharedGeofence }] = useUpdateSharedGeofenceMutation();
  const [deleteSharedGeofence, { isLoading: deletingSharedGeofence }] = useDeleteSharedGeofenceMutation();
  const [createLandmark, { isLoading: creatingLandmark }] = useCreateLandmarkMutation();
  const [updateLandmark, { isLoading: updatingLandmark }] = useUpdateLandmarkMutation();
  const [deleteLandmark, { isLoading: deletingLandmark }] = useDeleteLandmarkMutation();
  const [assignLandmarkDevice] = useAssignLandmarkDeviceMutation();
  const [unassignLandmarkDevice] = useUnassignLandmarkDeviceMutation();
  const [assignSharedGeofenceDevice] = useAssignSharedGeofenceDeviceMutation();
  const [unassignSharedGeofenceDevice] = useUnassignSharedGeofenceDeviceMutation();
  const { data: tripsData = [] } = useGetTripsQuery();
  const { data: routesDataRaw } = useGetRoutesQuery();
  const routesData = routesDataRaw?.data || routesDataRaw || [];

  const openTrips = useMemo(() => {
    const rows = Array.isArray(tripsData) ? tripsData : [];
    return rows.filter((t) => {
      const st = String(t?.tripStatus || t?.status || "").toLowerCase();
      return !["completed", "cancelled"].includes(st);
    });
  }, [tripsData]);

  const selectedDeviceTrip = useMemo(() => {
    if (!selectedDevice?._id) return null;
    const did = String(selectedDevice._id);
    const rows = openTrips
      .filter((t) => String(t?.vehicle?._id || t?.vehicle) === did)
      .sort((a, b) => {
        const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return bTime - aTime;
      });
    return rows[0] || null;
  }, [openTrips, selectedDevice?._id]);

  const selectedDeviceRoute = useMemo(() => {
    if (!selectedDevice?._id) return null;
    const trip = selectedDeviceTrip;
    if (!trip?.routeName || !Array.isArray(routesData)) return null;
    const wanted = String(trip.routeName).trim().toLowerCase();
    return (
      routesData.find((r) => String(r?.routeName || '').trim().toLowerCase() === wanted) ||
      null
    );
  }, [selectedDevice?._id, selectedDeviceTrip, routesData]);

  const mergedNotifications = useMemo(() => {
    const merged = new Map();

    const normalizeNotification = (alarm = {}) => {
      if (!alarm) return null;
      const alarmId = String(alarm?._id || alarm?.alarmId || alarm?.id || "");
      if (!alarmId) return null;

      const lat = Number(
        alarm?.location?.latitude ??
        alarm?.location?.lat ??
        alarm?.latitude ??
        alarm?.lat
      );
      const lng = Number(
        alarm?.location?.longitude ??
        alarm?.location?.lng ??
        alarm?.longitude ??
        alarm?.lng
      );

      return {
        ...alarm,
        _id: alarmId,
        alarmId,
        deviceId:
          alarm?.deviceId?._id ||
          alarm?.deviceId ||
          alarm?.device?._id ||
          alarm?.device ||
          null,
        deviceName:
          alarm?.deviceName ||
          alarm?.device?.name ||
          alarm?.device?.vehicleRegistration ||
          alarm?.vehicleRegistration ||
          null,
        imei: alarm?.imei || alarm?.device?.imei || null,
        status: alarm?.status || "active",
        location: {
          ...(alarm?.location || {}),
          latitude: Number.isFinite(lat) ? lat : null,
          longitude: Number.isFinite(lng) ? lng : null,
        },
      };
    };

    const baseNotifications = [
      ...(Array.isArray(alarms) ? alarms : []),
      ...(Array.isArray(selectedDeviceAlarmHistory) ? selectedDeviceAlarmHistory : []),
      ...(Array.isArray(liveAlarmNotifications) ? liveAlarmNotifications : []),
    ];

    baseNotifications.forEach((alarm) => {
      const normalized = normalizeNotification(alarm);
      if (!normalized) return;
      if (String(normalized.status || "").toLowerCase() === "resolved") return;
      merged.set(normalized.alarmId, normalized);
    });

    return Array.from(merged.values()).sort(
      (a, b) =>
        new Date(b?.triggeredAt || b?.timestamp || 0).getTime() -
        new Date(a?.triggeredAt || a?.timestamp || 0).getTime()
    );
  }, [alarms, liveAlarmNotifications, selectedDeviceAlarmHistory]);

  useEffect(() => {
    if (!on) return undefined;

    const normalizeLiveAlarm = (data) => {
      const alarm = data?.alarm || data;
      if (!alarm) return null;
      const alarmId = String(alarm?._id || alarm?.alarmId || alarm?.id || "");
      if (!alarmId) return null;
      return {
        ...alarm,
        _id: alarmId,
        alarmId,
      };
    };

    const offAlarmEvent = on("alarm_event", (data) => {
      const alarm = normalizeLiveAlarm(data);
      if (!alarm) return;
      if (
        alarm?.eventType === "alarm_acknowledged" ||
        alarm?.eventType === "alarm_resolved" ||
        alarm?.eventType === "alarm_status_update"
      ) {
        return;
      }
      if (String(alarm?.status || "").toLowerCase() === "resolved") return;

      setLiveAlarmNotifications((prev) => {
        const next = [alarm, ...prev.filter((item) => String(item?._id || item?.alarmId || item?.id || "") !== alarm._id)];
        return next.slice(0, 200);
      });
    });

    const offAlarmStatus = on("alarm_status_update", (data) => {
      const alarmId = String(data?.alarmId || data?._id || "");
      if (!alarmId) return;
      const nextStatus = String(data?.status || "").toLowerCase();

      setLiveAlarmNotifications((prev) => {
        if (nextStatus === "resolved") {
          return prev.filter((item) => String(item?._id || item?.alarmId || item?.id || "") !== alarmId);
        }
        return prev.map((item) =>
          String(item?._id || item?.alarmId || item?.id || "") === alarmId
            ? { ...item, status: data?.status || item?.status }
            : item
        );
      });
    });

    return () => {
      offAlarmEvent?.();
      offAlarmStatus?.();
    };
  }, [on]);

  // ================= EVENT HANDLERS =================

  const closeHistoryPlayback = useCallback(() => {
    // ✅ ADDED
    setShowHistory(false);
    setHistoryDevice(null);
  }, []);

  const closeReplayPlayback = useCallback(() => {
    setShowPlayback(false);
    setPlaybackData(null);
    setPlaybackFrameData(null);
    setBottomPanelHeight(0);
  }, []);

  const handleClientSubmit = async (data) => {
    try {
      await addClient(data).unwrap();
      showStatus("success", "Client added successfully");
      setShowClientModal(false);
    } catch (err) {
      console.error("Add Client Error:", err);
      showStatus("error", err?.data?.error || "Failed to add client");
    }
  };

  const handleReminderSubmit = async (data) => {
    // ✅ ADDED
    try {
      await addReminder(data).unwrap();
      showStatus("info", "Reminder set.");
      setShowReminderModal(false);
    } catch {
      showStatus("error", "Failed to set reminder.");
    }
  };
  // ================= EVENT HANDLERS =================

  // 🔥 ADD THIS HERE
  const openSidebarHistory = useCallback((device) => {
    if (!device) return;
    setHistoryDevice(device);
    setShowTripHistory(true);
    setShowInMapHistory(false);
    setShowPlayback(false);
  }, []);

  const handleMaterialSubmit = async (data) => {
    // ✅ ADDED
    try {
      await addMaterial(data).unwrap();
      showStatus("success", "Material added.");
      setShowMaterialModal(false);
    } catch {
      showStatus("error", "Failed to add material.");
    }
  };

  const handleLicenseSubmit = async (data) => {
    // ✅ ADDED
    try {
      const fd = new FormData();
      Object.keys(data).forEach((k) => {
        if (data[k]) fd.append(k, data[k]);
      });
      await addLicense(fd).unwrap();
      showStatus("success", "License uploaded.");
      setShowLicenseModal(false);
    } catch {
      showStatus("error", "Failed to upload license.");
    }
  };

  const handleDocumentSubmit = async (data) => {
    // ✅ ADDED
    try {
      const fd = new FormData();
      Object.keys(data).forEach((k) => {
        if (data[k]) fd.append(k, data[k]);
      });
      await addDocument(fd).unwrap();
      showStatus("success", "Document uploaded.");
      setShowDocumentModal(false);
    } catch {
      showStatus("error", "Failed to upload document.");
    }
  };

  const handleBottomExpandChange = useCallback((expanded, height) => {
    setIsBottomExpanded(expanded);
    if (height) setBottomPanelHeight(height);
  }, []);

  // Handle quick actions
useEffect(() => {
  if (!quickAction) return;

  const actionMap = {
    trip: () => openModal("trip"),
    route: () => openModal("route"),
    expense: () => openModal("expense"),
    driver: () => openModal("driver"),
    document: () => openModal("document"),
    material: () => openModal("material"),
    client: () => openModal("client"),
    sensor: () => openModal("sensor"),
    license: () => openModal("license"),
    reminder: () => openModal("reminder"),
    report: () => openModal("report", { allDevices: devicesOnMap, rawData: selectedDeviceData }),
  };

  actionMap[quickAction]?.();
  clearQuickAction();
}, [quickAction, clearQuickAction, openModal, devicesOnMap, selectedDeviceData]);

useEffect(() => {
  if (!externalRouteFocus) return;

  const sLat = Number(externalRouteFocus?.source?.lat);
  const sLng = Number(externalRouteFocus?.source?.lng);
  const eLat = Number(externalRouteFocus?.destination?.lat);
  const eLng = Number(externalRouteFocus?.destination?.lng);

  if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) {
    showStatus("warning", "Route coordinates are missing.");
    clearExternalRouteFocus?.();
    return;
  }

  setSelectedTripForMap(null);
  setSelectedRouteForMap(externalRouteFocus);

  if (map && window.google?.maps?.LatLngBounds) {
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: sLat, lng: sLng });
    bounds.extend({ lat: eLat, lng: eLng });
    map.fitBounds(bounds);
  }

  showStatus("info", `Route loaded: ${externalRouteFocus?.routeName || "Route"}`);
  clearExternalRouteFocus?.();
}, [externalRouteFocus, clearExternalRouteFocus, map, showStatus]);

const handleReplayTripFromReport = useCallback(async (payload) => {
  const row = payload?.tripRow || {};
  const replayData = row?.replayData || {};
  const replayLookupCandidates = [
    replayData?.deviceId,
    row?.deviceId,
    row?.device?._id,
    payload?.selectedDeviceId,
    replayData?.imei,
    row?.imei,
    row?.device?.imei,
    payload?.selectedDeviceImei,
    payload?.selectedImei,
    row?.vehicleNumber,
    row?.deviceName,
    payload?.vehicleName,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const replayDevicePool = [
    ...(Array.isArray(sidebarDevices) ? sidebarDevices : []),
    ...(Array.isArray(devicesOnMap) ? devicesOnMap : []),
  ];
  const matchesReplayCandidate = (device, lookupKey) => {
    if (!lookupKey) return false;
    const deviceId = String(device?._id || "").trim();
    const deviceImei = String(device?.imei || "").trim();
    const deviceName = String(device?.name || device?.vehicleName || "").trim();
    const vehicleNumber = String(device?.vehicleNumber || device?.registrationNumber || "").trim();
    return (
      deviceId === lookupKey ||
      deviceImei === lookupKey ||
      deviceName === lookupKey ||
      vehicleNumber === lookupKey
    );
  };
  const trustedSelectedDevice =
    payload?.selectedDevice?._id &&
    (
      !replayLookupCandidates.length ||
      replayLookupCandidates.some((lookupKey) => matchesReplayCandidate(payload.selectedDevice, lookupKey))
    )
      ? payload.selectedDevice
      : null;
  const candidateDevice =
    trustedSelectedDevice ||
    replayDevicePool.find((device) =>
      replayLookupCandidates.some((lookupKey) => matchesReplayCandidate(device, lookupKey))
    );

  if (!candidateDevice?._id) {
    showStatus("warning", "Replay failed: device not found.");
    return false;
  }

  const startRaw =
    replayData?.startTime ||
    payload?.startTimestamp ||
    row.startTimestamp ||
    row.startTs ||
    row.startTime;
  const endRaw =
    replayData?.endTime ||
    payload?.endTimestamp ||
    row.endTimestamp ||
    row.endTs ||
    row.endTime ||
    startRaw;
  const startDate = toYMD(startRaw);
  const endDate = toYMD(endRaw);

  if (!startDate || !endDate) {
    showStatus("warning", "Replay failed: start/end time missing.");
    return false;
  }

  setShowPlayback(false);
  setPlaybackData(null);
  setPlaybackFrameData(null);
  setSelectedTripForMap(null);
  setSelectedRouteForMap(null);
  setShowTripHistory(false);
  setShowInMapHistory(false);
  setHistoryDevice(null);
  setSidebarOpen(true);
  setSelectedImei(candidateDevice?.imei || null);
  setSelectedDevice(candidateDevice);
  setSelectedDevices(candidateDevice?._id ? [String(candidateDevice._id)] : []);
  setSidebarHistorySelection({
    deviceId: String(candidateDevice?._id || ""),
    deviceName:
      candidateDevice?.name ||
      candidateDevice?.vehicleNumber ||
      candidateDevice?.imei ||
      payload?.vehicleName ||
      row?.deviceName ||
      "",
    requestedAt: Date.now(),
    playbackMode: "report",
    startTimestamp: startRaw,
    endTimestamp: endRaw,
    startDate,
    endDate,
  });
  showStatus("info", "Opening history playback...");
  return true;
}, [devicesOnMap, sidebarDevices, showStatus]);

useEffect(() => {
  if (!externalPlaybackFocus) return;
  handleReplayTripFromReport(externalPlaybackFocus);
  clearExternalPlaybackFocus?.();
}, [externalPlaybackFocus, clearExternalPlaybackFocus, handleReplayTripFromReport]);

useEffect(() => {
  if (!connected) return;

const nextImeis = new Set(
  visibleMapVehicleImeis
);
selectedDevices.forEach((deviceId) => {
  const selectedDevice =
    mapVehicles.find((device) => String(device?._id) === String(deviceId)) ||
    (Array.isArray(sidebarDevices) ? sidebarDevices : []).find(
      (device) => String(device?._id) === String(deviceId)
    ) ||
    devicesOnMap.find((device) => String(device?._id) === String(deviceId));

  if (selectedDevice?.imei) {
    nextImeis.add(String(selectedDevice.imei).trim());
  }
});
  const prevImeis = subscribedImeisRef.current;
  const imeisToAdd = [];
  const imeisToRemove = [];

  nextImeis.forEach((imei) => {
    if (!prevImeis.has(imei)) {
      imeisToAdd.push(imei);
    }
  });

  prevImeis.forEach((imei) => {
    if (!nextImeis.has(imei)) {
      imeisToRemove.push(imei);
    }
  });

  if (imeisToAdd.length) {
    if (typeof subscribeToDevices === "function") subscribeToDevices(imeisToAdd);
    else imeisToAdd.forEach((imei) => subscribeToDevice(imei));
  }
  if (imeisToRemove.length) {
    if (typeof unsubscribeFromDevices === "function") unsubscribeFromDevices(imeisToRemove);
    else imeisToRemove.forEach((imei) => unsubscribeFromDevice(imei));
  }

  subscribedImeisRef.current = nextImeis;
}, [
  connected,
  visibleMapVehicleImeis,
  mapVehicles,
  sidebarDevices,
  devicesOnMap,
  selectedDevices,
  subscribeToDevice,
  subscribeToDevices,
  unsubscribeFromDevice,
  unsubscribeFromDevices,
]);
useEffect(() => {
  return () => {
    subscribedImeisRef.current.forEach((imei) => unsubscribeFromDevice(imei));
    subscribedImeisRef.current.clear();
  };
}, [unsubscribeFromDevice]);

const [tick, setTick] = useState(0);

const toYMD = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

useEffect(() => {
  // Low-frequency heartbeat only. Packets flow directly into MapContainer
  // via `gps_packet` → imperative marker updates + rAF interpolation.
  // Re-rendering MapTracker on every socket packet was resetting segment
  // interpolation mid-lerp, causing markers to jump every ~500ms.
  const id = setInterval(() => setTick((v) => v + 1), 5000);
  return () => clearInterval(id);
}, []);

useEffect(() => {
  if (!on) return undefined;
  // Coalesce socket bursts into at most one re-render every 1.5s so the
  // status pill / speed label / popup data don't feel frozen between 5s
  // heartbeats. Marker position still animates via gps_packet + rAF, so
  // this extra tick is safe — it no longer resets mid-lerp segments.
  let timer = null;
  let pending = false;
  const bump = () => {
    pending = true;
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      if (pending) {
        pending = false;
        setTick((v) => v + 1);
      }
    }, 1500);
  };
  const offs = [
    on("gps_packet", bump),
    on("location_update", bump),
    on("gps_update", bump),
    on("status_update", bump),
    on("vehicle_update", bump),
    on("device_update", bump),
    on("ignition_event", bump),
  ];
  return () => {
    offs.forEach((fn) => fn?.());
    if (timer) clearTimeout(timer);
  };
}, [on]);

useEffect(() => {
  const offConnection = on("socket_connection", (payload) => {
    const status = payload?.status || "unknown";
    if (status === connectionToastRef.current) return;
    connectionToastRef.current = status;
  });

  return () => offConnection?.();
}, [on]);




useEffect(() => {
  if (!followMode || !selectedDevice || !map) return;
  // MapContainer handles single-device auto-follow.
  if (selectedDevices.length === 1) return;

  const socketData =
    socketUpdatesRef.current[selectedDevice._id] ||
    socketUpdatesRef.current[selectedDevice.imei] ||
    {};
  const socketLocation = getSocketMapLocation(socketData);
  const lng = socketLocation.longitude ?? selectedDevice.currentLocation?.longitude ?? selectedDevice.liveData?.longitude;
  const latFallback = selectedDevice.currentLocation?.latitude ?? selectedDevice.liveData?.latitude;
  const latResolved = socketLocation.latitude ?? latFallback;

  if (isValidGPS(Number(latResolved), Number(lng))) {
    map.panTo({ lat: Number(latResolved), lng: Number(lng) });
  }
}, [followMode, selectedDevice, map, selectedDevices.length, socketUpdatesRef]);


const filteredDevices = useMemo(() => {
  const effectiveMapDevices = (() => {
    // The map intentionally renders only the cumulative pages loaded in the
    // sidebar. This prevents a 3,000-device fleet from rendering at once.
    if (Array.isArray(sidebarLoadedDevices) && sidebarLoadedDevices.length > 0) {
      return sidebarLoadedDevices;
    }
    const fallbackDevices =
      Array.isArray(sidebarDevices) && sidebarDevices.length > 0
        ? sidebarDevices
        : Array.isArray(devicesOnMap)
          ? devicesOnMap
          : [];
    if (placesViewActive) {
      return fallbackDevices;
    }
    const hasViewportVehicles = mapMode === "vehicles" && mapVehicles.length > 0;
    const hasViewportClusters = mapMode === "cluster" && mapClusters.length > 0;
    const hasSearchQuery = Boolean(String(searchTerm || "").trim());
    const base = hasViewportVehicles
      ? mapVehicles
      : hasViewportClusters
        ? []
      : !hasSearchQuery
        ? fallbackDevices
        : [];


    if (selectedDevices.length !== 1) return base;

    const selectedId = String(selectedDevices[0] || "");
    if (!selectedId) return base;

    const sidebarMatch = (Array.isArray(sidebarDevices) ? sidebarDevices : []).find(
      (device) => String(device?._id || device?.imei || "") === selectedId
    );

    if (!sidebarMatch) return base;

    let didReplace = false;
    const mergedBase = base.map((device) => {
      if (String(device?._id || device?.imei || "") !== selectedId) return device;
      didReplace = true;
      return {
        ...device,
        ...sidebarMatch,
        deviceLive: sidebarMatch?.deviceLive || device?.deviceLive,
        liveData: sidebarMatch?.liveData || device?.liveData,
        currentLocation: sidebarMatch?.currentLocation || device?.currentLocation,
      };
    });

    return didReplace ? mergedBase : [...base, sidebarMatch];
  })();

  // A selected vehicle represents the user's active tracking target. Status
  // tabs replace the sidebar page (Running -> Idle, etc.), so explicitly keep
  // every selected target in the map source even when it is not on that page.
  const selectedIds = new Set(selectedDevices.map((id) => String(id || "")).filter(Boolean));
  const effectiveIds = new Set(
    effectiveMapDevices.map((device) => String(device?._id || device?.imei || "")).filter(Boolean)
  );
  const selectedCandidates = [
    ...(Array.isArray(devicesOnMap) ? devicesOnMap : []),
    ...(Array.isArray(sidebarDevices) ? sidebarDevices : []),
    ...(Array.isArray(mapVehicles) ? mapVehicles : []),
  ];
  const selectedDevicesMissingFromPage = selectedCandidates.filter((device) => {
    const id = String(device?._id || device?.imei || "");
    if (!id || !selectedIds.has(id) || effectiveIds.has(id)) return false;
    effectiveIds.add(id);
    return true;
  });

  return [...effectiveMapDevices, ...selectedDevicesMissingFromPage].filter(device => {
    const deviceId = String(device?._id || device?.imei || "");
    const isSelected = selectedIds.has(deviceId);
    const socketData =
      socketUpdatesRef.current[device._id] ||
      socketUpdatesRef.current[device.imei] ||
      {};
    const socketLocation = getSocketMapLocation(socketData);
    const deviceStatus = getDeviceStatus(device, socketData);
    if (!isSelected && deviceStatus === 'idle' && !showIdleVehicles) return false;
    if (!isSelected && deviceStatus === 'running' && !showMovingVehicles) return false;
    if (!isSelected && deviceStatus === 'stopped' && !showStoppedVehicles) return false;
    if (!isSelected && deviceStatus === 'inactive' && !showInactiveVehicles) return false;
    
    const apiLive = device.deviceLive || {};
  const lat =
  socketLocation.latitude ??
  apiLive?.latitude ??
  device.currentLocation?.latitude ??
  device.currentLocation?.lat ??
  device.liveData?.latitude ??
  device.liveData?.lat ??
  device.geoLocation?.coordinates?.[1] ??
  device.location?.latitude ??
  device.location?.lat ??
  device.latitude ??
  device.lat;

const lng =
  socketLocation.longitude ??
  apiLive?.longitude ??
  device.currentLocation?.longitude ??
  device.currentLocation?.lng ??
  device.liveData?.longitude ??
  device.liveData?.lng ??
  device.geoLocation?.coordinates?.[0] ??
  device.location?.longitude ??
  device.location?.lng ??
  device.longitude ??
  device.lng;
    const hasGPS = isValidGPS(Number(lat), Number(lng));
    return hasGPS || deviceStatus === 'no_data' || deviceStatus === 'nodata' || deviceStatus === 'unknown';
  });
}, [
  devicesOnMap,
  sidebarLoadedDevices,
  sidebarDevices,
  selectedDevices,
  showIdleVehicles,
  showMovingVehicles,
  showStoppedVehicles,
  showInactiveVehicles,
  tick, // ✅ ADD THIS - forces refresh when socket updates
  mapMode,
  mapVehicles,
  mapClusters,
  placesViewActive,
  searchTerm,
]);

const mapSidebarDevices = useMemo(() => {
  const sourceDevices =
    Array.isArray(sidebarDevices) && sidebarDevices.length > 0
      ? sidebarDevices
      : devicesOnMap;
  return sourceDevices.filter((device) => {
    const socketData =
      socketUpdatesRef.current[device._id] ||
      socketUpdatesRef.current[device.imei] ||
      {};
    const deviceStatus = getDeviceStatus(device, socketData);

    if (deviceStatus === "idle" && !showIdleVehicles) return false;
    if (deviceStatus === "running" && !showMovingVehicles) return false;
    if (deviceStatus === "stopped" && !showStoppedVehicles) return false;
    if (deviceStatus === "inactive" && !showInactiveVehicles) return false;

    return true;
  });
}, [
  devicesOnMap,
  sidebarDevices,
  showIdleVehicles,
  showMovingVehicles,
  showStoppedVehicles,
  showInactiveVehicles,
  tick,
]);

const mapFocusedDevices = useMemo(() => {
  if (selectedDevices.length === 0) {
    return filteredDevices;
  }

if (selectedDevices.length === 1) {
  const selectedId = String(selectedDevices[0] || "");
  if (!selectedId) return filteredDevices;

  const selectedFromFiltered = filteredDevices.filter(
    (device) => String(device?._id || device?.imei || "") === selectedId
  );

  if (selectedFromFiltered.length) return selectedFromFiltered;

  const selectedFromSidebar = (Array.isArray(sidebarDevices) ? sidebarDevices : []).find(
    (device) => String(device?._id || device?.imei || "") === selectedId
  );

  return selectedFromSidebar ? [selectedFromSidebar] : selectedFromFiltered;
}

  const selectedIds = new Set(
    selectedDevices.map((id) => String(id || "")).filter(Boolean)
  );
  return filteredDevices.filter((device) =>
    selectedIds.has(String(device?._id || device?.imei || ""))
  );
}, [filteredDevices, selectedDevices]);

const effectiveMapMode = useMemo(
  () => (placesViewActive ? "cluster" : sidebarLoadedDevices.length ? "vehicles" : mapMode),
  [mapMode, placesViewActive, sidebarLoadedDevices.length]
);

const effectiveServerClusters = useMemo(
  () => (placesViewActive || sidebarLoadedDevices.length ? [] : mapClusters),
  [mapClusters, placesViewActive, sidebarLoadedDevices.length]
);

useEffect(() => {
  if (selectedDevices.length === 0 && selectedImei) {
    setSelectedImei(null);
  }
}, [selectedDevices, selectedImei]);

  const resolveDeviceMapState = useCallback((device) => {
    if (!device?._id && !device?.imei) return null;

    const socketData =
      socketUpdatesRef.current[device._id] ||
      socketUpdatesRef.current[device.imei] ||
      {};
    const socketLocation = getSocketMapLocation(socketData);
    const apiLive = device.deviceLive || {};
 const lat =
  socketLocation.latitude ??
  apiLive?.latitude ??
  device.currentLocation?.latitude ??
  device.currentLocation?.lat ??
  device.liveData?.latitude ??
  device.liveData?.lat ??
  device.geoLocation?.coordinates?.[1] ??
  device.location?.latitude ??
  device.location?.lat ??
  device.latitude ??
  device.lat;

const lng =
  socketLocation.longitude ??
  apiLive?.longitude ??
  device.currentLocation?.longitude ??
  device.currentLocation?.lng ??
  device.liveData?.longitude ??
  device.liveData?.lng ??
  device.geoLocation?.coordinates?.[0] ??
  device.location?.longitude ??
  device.location?.lng ??
  device.longitude ??
  device.lng;

    if (!isValidGPS(Number(lat), Number(lng))) return null;

    return {
      lat: Number(lat),
      lng: Number(lng),
      status: getDeviceStatus(device, socketData),
      isLive: isLiveGps(device),
    };
  }, [socketUpdatesRef]);

  // Get focused device
  const focusedDevice = useMemo(() => {
    if (selectedDevices.length !== 1) return null;
    const selectedId = String(selectedDevices[0] || "");
  return (
    mapVehicles.find((d) => String(d?._id || d?.imei || "") === selectedId) ||
  devicesOnMap.find((d) => String(d?._id || d?.imei || "") === selectedId) ||
  (Array.isArray(sidebarDevices) ? sidebarDevices : []).find(
        (d) => String(d?._id || d?.imei || "") === selectedId
      ) ||
      null
    );
}, [selectedDevices, mapVehicles, devicesOnMap, sidebarDevices]);
  // Sidebar source devices are filtered inside DeviceSidebar to avoid duplicating
  // search/status/group computations both here and in the sidebar.
  const sidebarSourceDevices = useMemo(() => {
    if (Array.isArray(sidebarDevices) && sidebarDevices.length > 0) {
      return sidebarDevices;
    }
    return mapSidebarDevices;
  }, [sidebarDevices, mapSidebarDevices]);

  // ================= EVENT HANDLERS (CONTINUED) =================
  const focusDevice = useCallback(
    (device, { replaceSelection = false } = {}) => {
      const clickedImei = String(device?.imei || "").trim();
      const deviceId = device?._id;
      if (!deviceId) return;

      setSelectedGeofence(null);
      if (replaceSelection) {
        setSelectedDevices([deviceId]);
      }

      setSelectedImei(clickedImei || null);
      setSelectedDevice(device);
      if (isMobile) setMobilePanelTab("info");
      mobileAutoCenterRef.current = deviceId;

      const mapState = resolveDeviceMapState(device);
      if (map && mapState) {
        map.panTo({ lat: mapState.lat, lng: mapState.lng });
        const currentZoom = Number(map.getZoom?.() ?? 0);
        if (currentZoom < FOLLOW_SELECTION_MIN_ZOOM) {
          map.setZoom(FOLLOW_SELECTION_MIN_ZOOM);
        }

        if (mapState.isLive && !isMobile) {
          setFollowMode(true);
          showStatus("info", `Following ${device.name}`);
        } else {
          setFollowMode(false);
        }
      }
    },
    [isMobile, map, resolveDeviceMapState, showStatus],
  );

  const handleDeviceClick = useCallback(
    (device) => {
      const deviceId = String(device?._id || device?.imei || "");
      if (!deviceId) return;

      // A row click is a single-vehicle map view. Checkbox interactions keep
      // their existing multi-select semantics, but clicking the row itself
      // must remove every other marker from the active map selection.
      setFocusedDeviceId(deviceId);
      focusDevice(device, { replaceSelection: true });
    },
    [focusDevice],
  );

  const setFocusedDevices = useCallback((nextSelection) => {
    setFocusedDeviceId((currentFocus) => {
      const resolved =
        typeof nextSelection === "function"
          ? nextSelection(currentFocus ? [currentFocus] : [])
          : nextSelection;
      return String(Array.isArray(resolved) ? resolved[0] || "" : "");
    });
  }, []);

  useEffect(() => {
    if (!focusedDeviceId || selectedDevices.length === 0) return;
    if (!selectedDevices.some((id) => String(id) === String(focusedDeviceId))) {
      setFocusedDeviceId("");
    }
  }, [focusedDeviceId, selectedDevices]);

  const handleDeviceFocus = useCallback(
    (device) => {
      focusDevice(device, { replaceSelection: false });
    },
    [focusDevice],
  );

  const handleSidebarFocusToggle = useCallback(
    (device) => {
      const deviceId = String(device?._id || device?.imei || "");
      if (!deviceId) return;

      if (String(sidebarFocusedDeviceId) === deviceId) {
        setSidebarFocusedDeviceId("");
        setFocusedDeviceId("");
        return;
      }

      setSidebarFocusedDeviceId(deviceId);
      setFocusedDeviceId(deviceId);
      setSelectedDevices((currentSelection = []) => {
        // Empty means "show all". For a restricted checkbox selection, make
        // the focused vehicle visible before asking MapContainer to highlight it.
        if (currentSelection.length === 0 || currentSelection.some((id) => String(id) === deviceId)) {
          return currentSelection;
        }
        return [...currentSelection, deviceId];
      });
      handleDeviceFocus(device);
    },
    [handleDeviceFocus, sidebarFocusedDeviceId]
  );

  const getFocusedDeviceBounds = useCallback(() => {
    if (!map || !window.google?.maps?.LatLngBounds || mapFocusedDevices.length === 0) {
      return null;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let validCount = 0;

    mapFocusedDevices.forEach((d) => {
      const socketData =
        socketUpdatesRef.current[d._id] ||
        socketUpdatesRef.current[d.imei] ||
        {};
      const socketLocation = getSocketMapLocation(socketData);
      const apiLive = d.deviceLive || {};
      const lat =
        socketLocation.latitude ??
        apiLive?.latitude ??
        d.currentLocation?.latitude ??
        d.currentLocation?.lat ??
        d.liveData?.latitude ??
        d.liveData?.lat ??
        d.geoLocation?.coordinates?.[1] ??
        d.location?.latitude ??
        d.location?.lat ??
        d.latitude ??
        d.lat;

      const lng =
        socketLocation.longitude ??
        apiLive?.longitude ??
        d.currentLocation?.longitude ??
        d.currentLocation?.lng ??
        d.liveData?.longitude ??
        d.liveData?.lng ??
        d.geoLocation?.coordinates?.[0] ??
        d.location?.longitude ??
        d.location?.lng ??
        d.longitude ??
        d.lng;

      if (isValidGPS(Number(lat), Number(lng))) {
        bounds.extend({
          lat: Number(lat),
          lng: Number(lng),
        });
        validCount += 1;
      }
    });

    return validCount ? bounds : null;
  }, [map, mapFocusedDevices]);

  const handleFitBounds = useCallback(() => {
    const bounds = getFocusedDeviceBounds();
    if (!map || !bounds) return;

    map.fitBounds(bounds, {
      top: 72,
      right: 72,
      bottom: 72,
      left: 72,
    });
    setFollowMode(false);
    setPlacesViewActive(false);
  }, [getFocusedDeviceBounds, map]);

  const handleTogglePlacesView = useCallback(() => {
    if (!map) return;

    const bounds = getFocusedDeviceBounds();
    if (placesViewActive) {
      if (!bounds) return;

      map.fitBounds(bounds, {
        top: 72,
        right: 72,
        bottom: 72,
        left: 72,
      });

      if (window.google?.maps?.event) {
        window.google.maps.event.addListenerOnce(map, "idle", () => {
          const zoom = Number(map.getZoom?.() || 0);
          if (zoom > 15) {
            map.setZoom(15);
          } else if (zoom < 10) {
            map.setZoom(10);
          }
        });
      }

      setPlacesViewActive(false);
    } else {
      map.panTo(INDIA_MAP_CENTER);
      map.setZoom(INDIA_OVERVIEW_ZOOM);
      setPlacesViewActive(true);
    }

    setFollowMode(false);
  }, [getFocusedDeviceBounds, map, placesViewActive]);

  const handleToggleFullscreen = useCallback(async () => {
    const container = layoutRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen?.();
      } else {
        await container.requestFullscreen?.();
      }
    } catch (error) {
      console.error("Fullscreen toggle failed", error);
      showStatus("error", "Fullscreen mode open nahi ho paaya.");
    }
  }, [showStatus]);

  const handleLocateMe = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (map) {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            map.panTo(pos);
            map.setZoom(14);
            setFollowMode(false);
            showStatus("info", "Your location found!");
          }
        },
        () => {
          showStatus("error", "Unable to get your location");
        },
      );
    }
  }, [map, showStatus]);

  const handleShowPlayback = useCallback(
    (deviceIdOrData) => {
      setShowHistory(false);
      setShowTripHistory(false);
      setShowInMapHistory(false);
      setHistoryDevice(null);
      setSelectedDevice(null);
      setSelectedDevices([]);
      setBottomPanelHeight(0);

      if (typeof deviceIdOrData === "object" && deviceIdOrData.route) {
        const playbackTrip = deviceIdOrData;
        const playbackDeviceId = String(playbackTrip?.deviceId || playbackTrip?._id || "");
        const fallbackDevice =
          devicesOnMap.find(
            (d) =>
              String(d?._id || "") === playbackDeviceId ||
              (playbackTrip?.imei && String(d?.imei || "") === String(playbackTrip.imei))
          ) ||
          historyDevice ||
          focusedDevice ||
          selectedDevice ||
          null;

        // Play specific trip
        setPlaybackData({
          ...playbackTrip,
          deviceId:
            playbackTrip?.deviceId ||
            fallbackDevice?._id ||
            null,
          imei:
            playbackTrip?.imei ||
            fallbackDevice?.imei ||
            null,
          vehicleType:
            playbackTrip?.vehicleType ||
            playbackTrip?.deviceIcon ||
            fallbackDevice?.vehicleType ||
            fallbackDevice?.deviceIcon ||
            "car",
          deviceIcon:
            playbackTrip?.deviceIcon ||
            playbackTrip?.vehicleType ||
            fallbackDevice?.deviceIcon ||
            fallbackDevice?.vehicleType ||
            "car",
        });
        setPlaybackFrameData(null);
        setFollowMode(false);
        setShowPlayback(true);
        return;
      }

      // Play device history
      const device = devicesOnMap.find((d) => d._id === deviceIdOrData);
      if (
        !device ||
        !device.locationHistory ||
        device.locationHistory.length === 0
      ) {
        showStatus("warning", "No location history available");
        return;
      }

      const history = [...device.locationHistory].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      );

      const routePoints = history
        .map((point) => ({
          lat: point.latitude || point.coordinates[1],
          lng: point.longitude || point.coordinates[0],
          speed: point.speed || 0,
          heading: point.heading ?? point.course ?? point.bearing ?? 0,
          course: point.course ?? null,
          bearing: point.bearing ?? null,
          accStatus: point.accStatus ?? point.acc ?? point.ignition ?? null,
          vehicleStatus: point.vehicleStatus ?? point.status ?? null,
          timestamp: new Date(point.timestamp).getTime(),
        }))
        .filter((p) => isValidGPS(Number(p.lat), Number(p.lng)));

      setPlaybackData({
        id: `trip_${device._id}`,
        deviceId: device?._id || null,
        imei: device?.imei || null,
        route: routePoints,
        distance: 0,
        startTime: new Date(history[0].timestamp).toLocaleTimeString(),
        endTime: new Date(
          history[history.length - 1].timestamp,
        ).toLocaleTimeString(),
        vehicleType: device?.vehicleType || device?.deviceIcon || "car",
        deviceIcon: device?.deviceIcon || device?.vehicleType || "car",
      });
      setPlaybackFrameData(null);
      setFollowMode(false);
      setShowPlayback(true);
    },
    [devicesOnMap, focusedDevice, historyDevice, selectedDevice, setFollowMode, showStatus],
  );

  const handleStartTripForDevice = useCallback(
    async (device) => {
      if (!device?._id) return;

      const did = String(device._id);
      const rows = openTrips
        .filter((t) => String(t?.vehicle?._id || t?.vehicle) === did)
        .sort((a, b) => {
          const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
          const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
          return bTime - aTime;
        });

      const pendingTrip =
        rows.find((t) => ["planned", "scheduled", "started"].includes(String(t?.tripStatus || t?.status || "").toLowerCase())) ||
        rows[0];

      if (!pendingTrip?._id) {
        showStatus("warning", "No trip found for this vehicle. Create trip first.");
        return;
      }

      try {
        await startTripMutation(pendingTrip._id).unwrap();
        showStatus("success", "Trip started.");
        setSelectedTripForMap(pendingTrip);
      } catch (err) {
        showStatus("error", err?.data?.error || "Failed to start trip.");
      }
    },
    [openTrips, showStatus, startTripMutation],
  );

  const handleEndTripForDevice = useCallback(
    async (device) => {
      if (!device?._id) return;

      const did = String(device._id);
      const runningTrip = openTrips.find((t) => {
        const vehicleId = String(t?.vehicle?._id || t?.vehicle);
        const st = String(t?.tripStatus || t?.status || "").toLowerCase();
        return vehicleId === did && ["started", "in_progress", "active"].includes(st);
      });

      if (!runningTrip?._id) {
        showStatus("warning", "No active trip found for this vehicle.");
        return;
      }

      try {
        await completeTripMutation({ id: runningTrip._id }).unwrap();
        showStatus("success", "Trip completed.");
        setSelectedTripForMap(null);
      } catch (err) {
        showStatus("error", err?.data?.error || "Failed to complete trip.");
      }
    },
    [completeTripMutation, openTrips, showStatus],
  );

  const handleGetMyTrip = useCallback(
    (device) => {
      if (!device?._id) return;
      const did = String(device._id);
      const rows = openTrips
        .filter((t) => String(t?.vehicle?._id || t?.vehicle) === did)
        .sort((a, b) => {
          const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
          const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
          return bTime - aTime;
        });
      const trip = rows[0];
      if (!trip) {
        showStatus("warning", "No trip assigned on this vehicle.");
        return;
      }
      setSelectedTripForMap(trip);
      setSelectedRouteForMap(null);

      const sLat = Number(trip?.startPoint?.lat);
      const sLng = Number(trip?.startPoint?.lng);
      const eLat = Number(trip?.endPoint?.lat);
      const eLng = Number(trip?.endPoint?.lng);
      if (map && [sLat, sLng, eLat, eLng].every(Number.isFinite)) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: sLat, lng: sLng });
        bounds.extend({ lat: eLat, lng: eLng });
        map.fitBounds(bounds, 80);
      }
      showStatus("info", "Trip loaded on map.");
    },
    [map, openTrips, showStatus],
  );

  const handleGetMyRoute = useCallback(
    (device) => {
      if (!device?._id) return;

      const rows = Array.isArray(routesData) ? routesData : [];
      const assignedRouteId = String(
        device?.advancedSettings?.defaultRouteId?._id ||
          device?.advancedSettings?.defaultRouteId ||
          ""
      );
      const matchedRoute =
        rows.find((r) => String(r?._id || r?.id || "") === assignedRouteId) ||
        null;

      if (!matchedRoute) {
        setSelectedRoutesForMap([]);
        setSelectedRouteForMap(null);
        showStatus("warning", "Is vehicle ke liye abhi route assign nahi hai.");
        return;
      }

      const sLat = Number(matchedRoute?.source?.lat);
      const sLng = Number(matchedRoute?.source?.lng);
      const eLat = Number(matchedRoute?.destination?.lat);
      const eLng = Number(matchedRoute?.destination?.lng);
      if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) {
        showStatus("warning", "Assigned route me coordinates missing hain.");
        return;
      }

      const overlay = {
        id: String(matchedRoute?._id || matchedRoute?.id || matchedRoute?.routeName || device._id),
        name: matchedRoute?.routeName || "Route",
        start: { lat: sLat, lng: sLng },
        end: { lat: eLat, lng: eLng },
      };

      setSelectedRoutesForMap([overlay]);
      setSelectedRouteForMap(null);
      setSelectedTripForMap(null);

      if (map && window.google?.maps?.LatLngBounds) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(overlay.start);
        bounds.extend(overlay.end);
        map.fitBounds(bounds, 80);
      }

      showStatus("info", `Loaded route: ${matchedRoute?.routeName || "Route"}`);
    },
    [map, routesData, showStatus],
  );

  // const handleShowTripHistory = useCallback((device) => {
  //   if (!device) return;

  //   setHistoryDevice(device);
  //   setShowTripHistory(true);
  //   setShowPlayback(false);
  //   setSelectedDevices([device._id]);
  // }, []);

  const closeTripHistory = useCallback(() => {
    setShowTripHistory(false);
    setHistoryDevice(null);
  }, []);

  const handleShowInMapHistory = useCallback((device) => {
    if (!device) {
      console.error("No device provided");
      return;
    }

    setHistoryDevice(device);
    setShowInMapHistory(true);
  }, []);

  const handleOpenReport = useCallback((device) => {
    if (!device) return;

    const enrichedDevice = {
      ...device,
      locationHistory: device.locationHistory || [],
      eventHistory: device.eventHistory || [],
    };

    setSelectedDeviceData([[enrichedDevice]]);
    setShowReportModal(true);
  }, []);

  const sidebarGeofenceItems = useMemo(() => {
    return (Array.isArray(sharedGeofences) ? sharedGeofences : [])
      .map((item) => ({
        ...item,
        id: String(item?._id || item?.id || ""),
        assignedDeviceIds: Array.isArray(item?.assignedDeviceIds)
          ? item.assignedDeviceIds
          : Array.isArray(item?.assignedDevices)
            ? item.assignedDevices.map((entry) => String(entry?.deviceId || "")).filter(Boolean)
            : [],
        assignedCount: Number(
          item?.assignedCount ??
            (Array.isArray(item?.assignedDevices) ? item.assignedDevices.length : 0)
        ),
        radiusText:
          item?.type === "circle" && Number.isFinite(Number(item?.radius))
            ? Number(item.radius).toFixed(2)
            : "",
      }))
      .sort((a, b) => getRecencyTime(b) - getRecencyTime(a));
  }, [sharedGeofences]);

  const sidebarLandmarkItems = useMemo(() => {
    return (Array.isArray(landmarks) ? landmarks : [])
      .map((item, index) => ({
        ...item,
        id: String(item?._id || item?.id || `landmark-${index}`),
        _id: item?._id || item?.id || null,
        name: item?.name || `Landmark ${index + 1}`,
        category: item?.category || "landmark",
        radius: Number(item?.radius ?? item?.radiusMeters ?? 30),
        radiusText: Number(item?.radius ?? item?.radiusMeters ?? 30).toFixed(2),
        address: item?.address || "",
        description: item?.description || "",
        lat: Number(item?.lat ?? item?.latitude),
        lng: Number(item?.lng ?? item?.longitude),
        assignedDevices: Array.isArray(item?.assignedDevices) ? item.assignedDevices : [],
        assignedDeviceIds: Array.isArray(item?.assignedDeviceIds)
          ? item.assignedDeviceIds
          : Array.isArray(item?.assignedDevices)
            ? item.assignedDevices.map((entry) => String(entry?.deviceId || "")).filter(Boolean)
            : [],
        assignedCount: Number(
          item?.assignedCount ??
            (Array.isArray(item?.assignedDevices) ? item.assignedDevices.length : 0)
        ),
      }))
      .sort((a, b) => getRecencyTime(b) - getRecencyTime(a));
  }, [landmarks]);

  const assignableDevices = useMemo(() => {
    if (Array.isArray(sidebarDevices) && sidebarDevices.length) return sidebarDevices;
    if (Array.isArray(devicesOnMap) && devicesOnMap.length) return devicesOnMap;
    return [];
  }, [devicesOnMap, sidebarDevices]);

  const activeAssigningGeofence = useMemo(() => {
    const wantedId = assigningGeofence?._id || assigningGeofence?.id;
    if (!wantedId) return null;
    return (
      sidebarGeofenceItems.find((item) => String(item?._id || item?.id) === String(wantedId)) ||
      assigningGeofence
    );
  }, [assigningGeofence, sidebarGeofenceItems]);

  const activeAssigningLandmark = useMemo(() => {
    const wantedId = assigningLandmark?._id || assigningLandmark?.id;
    if (!wantedId) return null;
    return (
      sidebarLandmarkItems.find((item) => String(item?._id || item?.id) === String(wantedId)) ||
      assigningLandmark
    );
  }, [assigningLandmark, sidebarLandmarkItems]);

  const openCreateSharedGeofence = useCallback((shapeType) => {
    setSharedGeofenceMode("create");
    setSharedGeofenceShapeType(shapeType === "circle" ? "circle" : "polygon");
    setEditingSharedGeofence(null);
    setShowSharedGeofenceModal(true);
  }, []);

  const openPopupSharedGeofenceModal = useCallback(() => {
    if (!hasUserPermission(currentUser, "manageGeofences")) return;
    openCreateSharedGeofence("polygon");
  }, [currentUser, openCreateSharedGeofence]);

  const openEditSharedGeofence = useCallback((geofence) => {
    if (!geofence) return;
    setSharedGeofenceMode("edit");
    setSharedGeofenceShapeType(geofence?.type === "circle" ? "circle" : "polygon");
    setEditingSharedGeofence(geofence);
    setShowSharedGeofenceModal(true);
  }, []);

  const handleSaveSharedGeofence = useCallback(async (payload) => {
    try {
      if (sharedGeofenceMode === "edit" && editingSharedGeofence?._id) {
        await updateSharedGeofence({
          geofenceId: editingSharedGeofence._id,
          ...payload,
        }).unwrap();
        showStatus("success", "Geofence updated successfully.");
      } else {
        await createSharedGeofence(payload).unwrap();
        showStatus("success", "Geofence created successfully.");
      }
      setShowSharedGeofenceModal(false);
      setEditingSharedGeofence(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to save geofence.");
    }
  }, [createSharedGeofence, editingSharedGeofence, sharedGeofenceMode, showStatus, updateSharedGeofence]);

  const handleDeleteSharedGeofence = useCallback(async (geofence) => {
    if (!geofence?._id) return;
    try {
      await deleteSharedGeofence(geofence._id).unwrap();
      if (String(selectedGeofence?.id || "") === String(geofence._id)) {
        setSelectedGeofence(null);
      }
      showStatus("success", "Geofence deleted successfully.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete geofence.");
    }
  }, [deleteSharedGeofence, selectedGeofence, showStatus]);

  const openAssignSharedGeofence = useCallback((geofence) => {
    if (!geofence) return;
    setAssigningGeofence(geofence);
    setShowAssignGeofenceModal(true);
  }, []);

  const handleToggleSharedGeofenceDevice = useCallback(async (device, checked) => {
    const geofenceId = activeAssigningGeofence?._id || activeAssigningGeofence?.id;
    const deviceId = device?._id;
    if (!geofenceId || !deviceId) return;

    setPendingGeofenceDeviceIds((current) => ({ ...current, [deviceId]: true }));
    try {
      if (checked) {
        await assignSharedGeofenceDevice({ geofenceId, deviceId }).unwrap();
      } else {
        await unassignSharedGeofenceDevice({ geofenceId, deviceId }).unwrap();
      }
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update geofence assignment.");
    } finally {
      setPendingGeofenceDeviceIds((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
    }
  }, [activeAssigningGeofence, assignSharedGeofenceDevice, showStatus, unassignSharedGeofenceDevice]);

  const openGeofenceModal = useCallback(
    (device) => {
      if (!hasUserPermission(currentUser, "manageGeofences")) return;
      setSelectedDeviceForGeofence(device);
      setGeofenceName(device?.geofences?.[0]?.name || `${device.name} - Fence`);
      setUnsavedGeofence(null);
      setGeofenceSuccessMessage("");
      setGeofenceErrorMessage("");
      setShowGeofenceModal(true);
    },
    [currentUser],
  );

  const onGeofenceCreated = useCallback((geofenceData) => {
    setUnsavedGeofence(geofenceData);
    setGeofenceSuccessMessage("");
    setGeofenceErrorMessage("");
  }, []);

  const onGeofenceEdited = useCallback((geofenceData) => {
    setUnsavedGeofence(geofenceData);
    setGeofenceSuccessMessage("");
    setGeofenceErrorMessage("");
  }, []);

  const onGeofenceDeleted = useCallback(() => {
    setUnsavedGeofence(null);
    setGeofenceSuccessMessage("");
    setGeofenceErrorMessage("");
  }, []);

  const handleSaveGeofence = useCallback(async () => {
    if (!selectedDeviceForGeofence?._id || !unsavedGeofence || !geofenceName?.trim()) {
      setGeofenceErrorMessage("Please complete geofence details before saving.");
      showStatus("warning", "Please complete geofence details before saving.");
      return;
    }

    try {
      let payload;
      if (unsavedGeofence.type === "circle") {
        payload = {
          name: geofenceName.trim(),
          type: "circle",
          center: {
            lat: Number(unsavedGeofence?.data?.center?.lat ?? unsavedGeofence?.data?.center?.[0]),
            lng: Number(unsavedGeofence?.data?.center?.lng ?? unsavedGeofence?.data?.center?.[1]),
          },
          radius: Number(unsavedGeofence?.data?.radius || 0),
          isActive: true,
        };
      } else {
        const coords = (Array.isArray(unsavedGeofence?.data) ? unsavedGeofence.data : []).map((point) => [
          Number(point?.lng),
          Number(point?.lat),
        ]);
        if (
          coords.length &&
          (coords[0][0] !== coords[coords.length - 1][0] ||
            coords[0][1] !== coords[coords.length - 1][1])
        ) {
          coords.push(coords[0]);
        }
        payload = {
          name: geofenceName.trim(),
          type: "polygon",
          polygon: {
            type: "Polygon",
            coordinates: [coords],
          },
          isActive: true,
        };
      }

      await editDevice({
        deviceId: selectedDeviceForGeofence._id,
        geofences: [payload],
      }).unwrap();

      setGeofenceSuccessMessage("Geofence saved successfully.");
      setGeofenceErrorMessage("");
      showStatus("success", "Geofence saved successfully.");
      setShowGeofenceModal(false);
    } catch (err) {
      const message = err?.data?.error || err?.data?.msg || "Failed to save geofence.";
      setGeofenceErrorMessage(message);
      setGeofenceSuccessMessage("");
      showStatus("error", message);
    }
  }, [editDevice, geofenceName, selectedDeviceForGeofence, showStatus, unsavedGeofence]);

  const handleDeleteGeofence = useCallback(async () => {
    if (!selectedDeviceForGeofence?._id) return;

    try {
      await editDevice({
        deviceId: selectedDeviceForGeofence._id,
        geofences: [],
      }).unwrap();

      setUnsavedGeofence(null);
      setGeofenceName("");
      setGeofenceSuccessMessage("Geofence deleted successfully.");
      setGeofenceErrorMessage("");
      setShowGeofenceModal(false);
      showStatus("success", "Geofence deleted successfully.");
    } catch (err) {
      const message = err?.data?.error || err?.data?.msg || "Failed to delete geofence.";
      setGeofenceErrorMessage(message);
      setGeofenceSuccessMessage("");
      showStatus("error", message);
    }
  }, [editDevice, selectedDeviceForGeofence, showStatus]);

  const handleSidebarGeofenceSelect = useCallback((geofence) => {
    if (!geofence) {
      setSelectedGeofence(null);
      return;
    }
    setSelectedGeofence({
      ...geofence,
      id: String(geofence?.id || geofence?._id || ""),
    });
  }, []);

  const handleSidebarLandmarkSelect = useCallback((landmark) => {
    if (!landmark) {
      setSelectedLandmark(null);
      return;
    }
    setSelectedLandmark({
      ...landmark,
      id: String(landmark?.id || landmark?._id || ""),
    });
  }, []);

  const handleCreateSidebarLandmark = useCallback(async (payload) => {
    try {
      const response = await createLandmark(payload).unwrap();
      const savedLandmark = response?.poi || response?.data || response;
      if (savedLandmark) {
        setSelectedLandmark({
          ...savedLandmark,
          id: String(savedLandmark?._id || savedLandmark?.id || ""),
        });
      }
      showStatus("success", "Landmark created successfully.");
      return savedLandmark;
    } catch (error) {
      throw new Error(
        error?.data?.msg ||
        error?.data?.error ||
        error?.error ||
        "Failed to create landmark."
      );
    }
  }, [createLandmark, showStatus]);

  const handleUpdateSidebarLandmark = useCallback(async ({ landmarkId, ...payload }) => {
    try {
      const response = await updateLandmark({ landmarkId, ...payload }).unwrap();
      const savedLandmark = response?.poi || response?.data || response;
      if (savedLandmark) {
        setSelectedLandmark({
          ...savedLandmark,
          id: String(savedLandmark?._id || savedLandmark?.id || landmarkId || ""),
        });
      }
      showStatus("success", "Landmark updated successfully.");
      return savedLandmark;
    } catch (error) {
      throw new Error(error?.data?.msg || "Failed to update landmark.");
    }
  }, [showStatus, updateLandmark]);

  const handleDeleteSidebarLandmark = useCallback(async (landmark) => {
    const landmarkId = landmark?._id || landmark?.id;
    if (!landmarkId) return;
    try {
      await deleteLandmark(landmarkId).unwrap();
      if (String(selectedLandmark?.id || "") === String(landmarkId)) {
        setSelectedLandmark(null);
      }
      showStatus("success", "Landmark deleted successfully.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete landmark.");
    }
  }, [deleteLandmark, selectedLandmark, showStatus]);

  const openAssignLandmark = useCallback((landmark) => {
    if (!landmark) return;
    setAssigningLandmark(landmark);
    setShowAssignLandmarkModal(true);
  }, []);

  const handleToggleLandmarkDevice = useCallback(
    async (device, nextState = {}) => {
      const landmarkId = activeAssigningLandmark?._id || activeAssigningLandmark?.id;
      const deviceId = device?._id;
      if (!landmarkId || !deviceId) return;

      setPendingLandmarkDeviceIds((current) => ({ ...current, [deviceId]: true }));
      try {
        if (nextState.checked) {
          await assignLandmarkDevice({
            landmarkId,
            deviceId,
            entryAlert: Boolean(nextState.entryAlert),
            exitAlert: Boolean(nextState.exitAlert),
          }).unwrap();
        } else {
          await unassignLandmarkDevice({ landmarkId, deviceId }).unwrap();
        }
      } catch (error) {
        showStatus(
          "error",
          error?.data?.msg || "Failed to update landmark assignment."
        );
      } finally {
        setPendingLandmarkDeviceIds((current) => {
          const next = { ...current };
          delete next[deviceId];
          return next;
        });
      }
    },
    [activeAssigningLandmark, assignLandmarkDevice, showStatus, unassignLandmarkDevice]
  );

  const handleSidebarNotificationSelect = useCallback((alarm) => {
    if (!alarm) return;
    setSelectedNotification(alarm);

    const alarmDeviceId = String(alarm?.deviceId || "");
    const device = filteredDevices.find((item) => String(item?._id) === alarmDeviceId);
    if (device) {
      handleDeviceClick(device);
    }
  }, [filteredDevices, handleDeviceClick]);

  // Other modal handlers
  const handleTripSubmit = async (data) => {
    try {
      await addTrip(data).unwrap();
      showStatus("success", "Trip saved.");
      setShowTripModal(false);
    } catch {
      showStatus("error", "Failed to save trip.");
    }
  };

  const handleRouteSubmit = async (data) => {
    try {
      await addRoute(data).unwrap();
      showStatus("success", "Route saved.");
      setShowRouteModal(false);
    } catch {
      showStatus("error", "Failed to save route.");
    }
  };

  const handleSensorSubmit = async (data) => {
    try {
      await addSensor(data).unwrap();
      showStatus("success", "Sensor saved.");
      setShowSensorModal(false);
    } catch {
      showStatus("error", "Failed to save sensor.");
    }
  };

  const handleExpenseSubmit = async (data) => {
    try {
      const fd = new FormData();
      Object.keys(data).forEach((k) => {
        if (data[k]) fd.append(k, data[k]);
      });
      await addExpense(fd).unwrap();
      showStatus("success", "Expense saved.");
      setShowExpenseModal(false);
    } catch {
      showStatus("error", "Failed to save expense.");
    }
  };

  const handleDriverSubmit = async (data) => {
    try {
      await createUser({
        username: data.username,
        mobileNo: data.mobileNo,
        email: `${data.mobileNo}-${Date.now()}@driver.com`,
        password: data.password, // 👈 ab form se aa raha
        role: ROLES.VIEWER,
        uniqueId: data.uniqueId,
        address: data.address,
        licenseNo: data.licenseNo,
        licenseValidity: data.licenseValidity,
        salary: data.salary,
      }).unwrap();

      showStatus("success", "Driver added.");
      setShowDriverModal(false);
    } catch (err) {
      console.error(err);
      showStatus("error", err?.data?.msg || "Failed to add driver.");
    }
  };

  const handleSidebarEditDevice = useCallback((device) => {
    setEditingDevice(device);
    setShowRegisterDeviceModal(true);
  }, []);

  const handleMapVehicleHistory = useCallback((device) => {
    if (!device) return;
    const historyLabel =
      device.vehicleNumber ||
      device.vehicleNo ||
      device.registrationNo ||
      device.vehicleRegistration ||
      device.name ||
      device.imei ||
      "Vehicle";
    setSidebarHistorySelection({
      deviceId: device._id,
      deviceName: historyLabel,
      requestedAt: Date.now(),
    });
    setHistoryDevice(device);
    setShowHistory(false);
    setShowTripHistory(false);
    setShowInMapHistory(false);
    setSelectedDevice(null);
    setBottomPanelHeight(0);
    setPlaybackData({
      historyMode: true,
      deviceId: device._id,
      deviceName: historyLabel,
      vehicleType: device.vehicleType || device.deviceIcon || "car",
      deviceIcon: device.deviceIcon || device.vehicleType || "car",
      imei: device.imei || null,
      route: [],
    });
    setPlaybackFrameData(null);
    setFollowMode(false);
    setShowPlayback(true);
  }, []);

  const handleOpenRegisterDevice = useCallback(() => {
    setEditingDevice(null);
    setShowRegisterDeviceModal(true);
  }, []);

  const handleCloseRegisterDevice = useCallback(() => {
    setShowRegisterDeviceModal(false);
    setEditingDevice(null);
  }, []);

  useEffect(() => {
    const activeReplay = showPlayback ? (playbackFrameData || playbackData) : null;
    const replayActive = Boolean(
      showPlayback &&
      (activeReplay?.deviceId || activeReplay?.imei)
    );

    setIsReplayMode(replayActive);

    if (!replayActive) {
      setHiddenLiveMarkers((prev) => (prev.size ? new Set() : prev));
      return;
    }

    const nextHidden = new Set(
      devicesOnMap
        .map((device) => String(device?._id || device?.imei || "").trim())
        .filter(Boolean)
    );

    setHiddenLiveMarkers((prev) => {
      if (prev.size === nextHidden.size) {
        let identical = true;
        for (const markerId of nextHidden) {
          if (!prev.has(markerId)) {
            identical = false;
            break;
          }
        }
        if (identical) return prev;
      }
      return nextHidden;
    });
  }, [devicesOnMap, playbackData, playbackFrameData, showPlayback]);

  const liveSelectedDevice = useMemo(() => {
    if (!selectedDevice?._id) return null;
    const matched = devicesOnMap.find(
      (device) => String(device?._id || "") === String(selectedDevice?._id || ""),
    );
    if (!matched) return selectedDevice;
    const matchedDriver = matched?.driver;
    const selectedDriver = selectedDevice?.driver;
    const hasMatchedDriver = Boolean(
      matchedDriver?.driverName || matchedDriver?.contactNumber || matchedDriver?.driverId,
    );
    return {
      ...selectedDevice,
      ...matched,
      driver: hasMatchedDriver ? { ...(selectedDriver || {}), ...matchedDriver } : selectedDriver,
    };
  }, [devicesOnMap, selectedDevice]);

  const activePanelDevice = liveSelectedDevice || focusedDevice || null;
  const showSidebarPanel = !isViewer && (!isMobile || mobilePanelTab === "cars");
  const showInfoPanel = !isMobile || mobilePanelTab === "info";

  useEffect(() => {
    if (!isMobile || !map || showPlayback || showInMapHistory || showTripHistory) return;

    const preferredDevice =
      (activePanelDevice && (() => {
        const state = resolveDeviceMapState(activePanelDevice);
        return state && (state.status === "running" || state.status === "idle")
          ? { device: activePanelDevice, state }
          : null;
      })()) ||
      filteredDevices.reduce((found, device) => {
        if (found) return found;
        const state = resolveDeviceMapState(device);
        if (!state) return null;
        return state.status === "running" ? { device, state } : null;
      }, null);

    if (!preferredDevice?.device?._id || !preferredDevice?.state) return;

    const deviceKey = String(preferredDevice.device._id);
    if (mobileAutoCenterRef.current === deviceKey) return;

    mobileAutoCenterRef.current = deviceKey;
    map.panTo({ lat: preferredDevice.state.lat, lng: preferredDevice.state.lng });
    const currentZoom = Number(map.getZoom?.() ?? 0);
    if (currentZoom < FOLLOW_SELECTION_MIN_ZOOM) {
      map.setZoom(FOLLOW_SELECTION_MIN_ZOOM);
    }
  }, [
    activePanelDevice,
    filteredDevices,
    isMobile,
    map,
    resolveDeviceMapState,
    showInMapHistory,
    showPlayback,
    showTripHistory,
  ]);

  useEffect(() => {
    if (isMobile && mobilePanelTab === "cars" && !sidebarOpen) {
      setSidebarOpen(true);
    }
  }, [isMobile, mobilePanelTab, sidebarOpen]);

  const handlePanelDeviceUpdated = useCallback((updatedDevice) => {
    if (!updatedDevice?._id) return;
    setSelectedDevice((prev) => {
      if (!prev || String(prev?._id || "") !== String(updatedDevice?._id || "")) {
        return prev;
      }
      return {
        ...prev,
        ...updatedDevice,
        driver: {
          ...(prev?.driver || {}),
          ...(updatedDevice?.driver || {}),
        },
      };
    });
  }, []);

  useEffect(() => {
    const selectedId = selectedDevices.length === 1 ? String(selectedDevices[0] || "") : "";
    const activeId = String(activePanelDevice?._id || "");

    if (!selectedId || selectedId !== activeId) {
      if (!selectedId) {
        autoFollowSelectionKeyRef.current = "";
      }
      return;
    }

    if (showPlayback || showTripHistory || showInMapHistory) return;

    if (autoFollowSelectionKeyRef.current === selectedId) return;

    autoFollowSelectionKeyRef.current = selectedId;
    setFollowMode(true);
  }, [
    activePanelDevice?._id,
    selectedDevices,
    showInMapHistory,
    showPlayback,
    showTripHistory,
  ]);

  // ================= RENDER =================
  if (!isCurrentMapRoute) return null;

  if (!isLoaded) return <div className="loading-screen">Loading Maps...</div>;

  const layoutStyle = {
    "--sidebar-width": sidebarOpen
      ? (isCompactDesktop ? "clamp(360px, 24vw, 430px)" : "clamp(480px, 28vw, 560px)")
      : "0px",
    "--right-panel-width": isCompactDesktop
      ? "clamp(280px, 18vw, 340px)"
      : "clamp(320px, 20vw, 420px)",
    "--right-rail-width": isCompactDesktop ? "2.5rem" : "3rem",
    "--map-shell-gap": isMobile ? "0.5rem" : (isCompactDesktop ? "0.625rem" : "0.875rem"),
    "--map-shell-padding": isMobile ? "0.5rem" : (isCompactDesktop ? "0.625rem" : "0.875rem"),
    "--compact-desktop": isCompactDesktop ? 1 : 0,
    "--navbar-offset": `${NAVBAR_OFFSET}px`,
    "--bottom-overlay-offset": `${bottomPanelHeight || 0}px`,
    paddingBottom: bottomPanelHeight || 0,
    boxSizing: "border-box",
    transition: "padding-bottom 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
  };

  return (
    <div
      ref={layoutRef}
      className={`dashboard-layout ${fullscreen ? "fullscreen" : ""}`}
      style={layoutStyle}
    >
      {!isMobile && !sidebarOpen && (
        <button
          className="ds-open-handle"
          onClick={() => setSidebarOpen(true)}
        >
          ›
        </button>
      )}

      {isMobile && (
        <div
          style={{
            position: "fixed",
            top: 74,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "min(calc(100vw - 24px), 320px)",
            padding: 6,
            borderRadius: 999,
            background: "rgba(15, 23, 42, 0.84)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 14px 28px rgba(15,23,42,0.24)",
          }}
        >
          {[
            { key: "cars", label: "Cars", disabled: false },
            { key: "info", label: "Info", disabled: !activePanelDevice },
          ].map((item) => {
            const active = mobilePanelTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => !item.disabled && setMobilePanelTab(item.key)}
                disabled={item.disabled}
                style={{
                  flex: 1,
                  height: 38,
                  border: "none",
                  borderRadius: 999,
                  background: active ? "#ffffff" : "transparent",
                  color: active ? "#111827" : item.disabled ? "rgba(255,255,255,0.38)" : "#ffffff",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: item.disabled ? "not-allowed" : "pointer",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}

      {showSidebarPanel && (
        <div className={`map-sidebar ${sidebarOpen ? "open" : "closed"}`}>
          <DeviceSidebar
            devices={sidebarSourceDevices}
            selectedDevices={selectedDevices}
            focusedDeviceId={sidebarFocusedDeviceId}
            deviceCounts={deviceCounts}
            totalDeviceCount={totalDeviceCount}
            initialStatus={initialSidebarStatus}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onDeviceClick={handleDeviceClick}
            onFocusDevice={handleDeviceClick}
            onLoadedDevicesChange={setSidebarLoadedDevices}
            setSelectedDevices={setSelectedDevices}
            onOpenPlayback={handleShowPlayback}
            onClosePlayback={() => {
              setShowPlayback(false);
              setPlaybackData(null);
              setPlaybackFrameData(null);
              setBottomPanelHeight(0);
            }}
            onToggleVisibility={handleSidebarFocusToggle}
            handleOpenCommandModal={
              hasUserPermission(currentUser, "sendCommands")
                ? handleOpenCommandModal
                : null
            }
            onOpenGeofence={
              hasUserPermission(currentUser, "manageGeofences")
                ? openGeofenceModal
                : null
            }
            onOpenReport={handleOpenReport}
            getVehicleIcon={getVehicleIcon}
            socketUpdatesRef={socketUpdatesRef}
            open={sidebarOpen}
            onToggle={setSidebarOpen}
            onEditDevice={handleSidebarEditDevice}
            onAddDevice={handleOpenRegisterDevice}
            onSelectGeofence={handleSidebarGeofenceSelect}
            selectedGeofenceId={selectedGeofence?.id || null}
            sidebarGeofences={sidebarGeofenceItems}
            onCreateSidebarGeofence={hasUserPermission(currentUser, "manageGeofences") ? openCreateSharedGeofence : null}
            onEditSidebarGeofence={hasUserPermission(currentUser, "manageGeofences") ? openEditSharedGeofence : null}
            onDeleteSidebarGeofence={hasUserPermission(currentUser, "manageGeofences") ? handleDeleteSharedGeofence : null}
            onAssignSidebarGeofence={hasUserPermission(currentUser, "manageGeofences") ? openAssignSharedGeofence : null}
            sidebarLandmarks={sidebarLandmarkItems}
            selectedLandmarkId={selectedLandmark?.id || null}
            onSelectLandmark={handleSidebarLandmarkSelect}
            onCreateSidebarLandmark={handleCreateSidebarLandmark}
            onUpdateSidebarLandmark={handleUpdateSidebarLandmark}
            onDeleteSidebarLandmark={handleDeleteSidebarLandmark}
            onAssignSidebarLandmark={
              hasUserPermission(currentUser, "manageGeofences")
                ? openAssignLandmark
                : null
            }
            landmarkSaving={creatingLandmark || updatingLandmark || deletingLandmark}
            currentContextDevice={activePanelDevice}
            onLandmarkDraftChange={setDraftLandmark}
            landmarkDraftValue={draftLandmark}
            onLandmarkFocusChange={setLandmarkFocusPoint}
            onLandmarkPlacementModeChange={setLandmarkPlacementActive}
            notifications={mergedNotifications}
            onSelectNotification={handleSidebarNotificationSelect}
            selectedNotificationId={selectedNotification?._id || selectedNotification?.alarmId || null}
            notificationsRefreshing={
              alarmsRefreshing || (Boolean(selectedDevice?._id) && selectedDeviceAlarmHistoryRefreshing)
            }
            onRefreshNotifications={() => {
              refetchAlarms();
              if (selectedDevice?._id) {
                refetchSelectedDeviceAlarmHistory();
              }
            }}
            historySelection={sidebarHistorySelection}
            mobileMode={isMobile}
            progressiveLoading={progressiveLoading}
          />
          {!isMobile && (
            <button
              className="sidebar-toggle-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? "◀" : "▶"}
            </button>
          )}
        </div>
      )}

      <div
        className="map-container"
  style={{
    transition: "none",
  }}
      >
<MapContainer
  map={map}
  setMap={setMap}
  tick={tick}
  playbackData={showPlayback ? (playbackFrameData || playbackData) : null}
  isReplayMode={isReplayMode}
  hiddenLiveMarkers={hiddenLiveMarkers}
  mapType={mapType}
  showTraffic={showTraffic}
  devicesOnMap={mapFocusedDevices}
  serverClusters={effectiveServerClusters}
  mapMode={effectiveMapMode}
  mapLoading={mapLoading}
  mapTotal={mapTotal}
  sharedGeofences={sidebarGeofenceItems}
  landmarks={sidebarLandmarkItems}
  draftLandmark={draftLandmark}
  landmarkFocusPoint={landmarkFocusPoint}
  landmarkPlacementActive={landmarkPlacementActive}
  onDraftLandmarkChange={setDraftLandmark}
  selectedDevices={focusedDeviceId ? [focusedDeviceId] : []}
  setSelectedDevices={setFocusedDevices}
  handleDeviceClick={handleDeviceClick}
  getVehicleIcon={getVehicleIcon}
  onDeviceClick={handleDeviceClick}
  openMoreMenu={(e, device) => {
    e.stopPropagation();
    console.log("More menu:", device);
  }}
  onEditDevice={handleSidebarEditDevice}
  onOpenGeofence={
    hasUserPermission(currentUser, "manageGeofences")
      ? openPopupSharedGeofenceModal
      : null
  }
  onShowDeviceHistory={handleMapVehicleHistory}
  onSendCommand={(device) => {
    if (handleOpenCommandModal && device) {
      handleOpenCommandModal(device);
    }
  }}
  setFollowMode={setFollowMode}
  followMode={followMode}
  routeStart={routeStart}
  routeEnd={routeEnd}
  setRouteDetails={setRouteDetails}
  socketUpdatesRef={socketUpdatesRef}
  showGeofences={true}
  selectedGeofence={selectedGeofence}
  selectedLandmark={selectedLandmark}
  onSelectLandmark={handleSidebarLandmarkSelect}
  forceTripId={selectedTripForMap?._id || null}
  forceTripVehicleId={selectedTripForMap?.vehicle?._id || selectedTripForMap?.vehicle || null}
  forceRoute={selectedRouteForMap}
  forceRoutes={selectedRoutesForMap}
  selectedNotification={selectedNotification}
  onClearSelectedNotification={() => setSelectedNotification(null)}
  clusterSourceDevices={[]}
  onViewportChange={(snapshot) => {
    handleMapViewportChange(snapshot);
    onMapViewportChange?.(snapshot);
  }}
  resolvedAddressHint={panelResolvedAddress}
/>
      </div>

      {showLayerPanel && (
        <MapLayerPanel
          onClose={() => setShowLayerPanel(false)}
          showTraffic={showTraffic}
          setShowTraffic={setShowTraffic}
          showLabels={showLabels}
          setShowLabels={setShowLabels}
          showPOI={showPOI}
          setShowPOI={setShowPOI}
        />
      )}
      {showInfoPanel && (
      <RightInfoPanel
          device={activePanelDevice}
          socketUpdatesRef={socketUpdatesRef}
          onDeviceUpdated={handlePanelDeviceUpdated}
          onResolvedAddressChange={setPanelResolvedAddress}
          isOpen
          onExpandedChange={handleBottomExpandChange}
          isExpanded={isBottomExpanded}
          sidebarOpen={sidebarOpen} // 👈 PASS THIS PROP
          onClose={() => {
            setSelectedImei(null);
            setSelectedDevice(null);
            setSelectedDevices([]);
            setPanelResolvedAddress("");
            if (isMobile) setMobilePanelTab("cars");
          }}
          showTraffic={showTraffic}
          setShowTraffic={setShowTraffic}
          mapType={mapType}
          setMapType={setMapType}
          followMode={followMode}
          setFollowMode={setFollowMode}
          routesOverlayActive={selectedRoutesForMap.length > 0}
          hasSelectedDevice={!!activePanelDevice}
          onLocateMe={handleLocateMe}
          onFitBounds={handleFitBounds}
          onTogglePlacesView={handleTogglePlacesView}
          placesViewActive={placesViewActive}
          fullscreen={fullscreen}
          onToggleFullscreen={handleToggleFullscreen}
          onZoomIn={() => {
            if (!map) return;
            map.setZoom(Math.min((map.getZoom?.() || 0) + 1, 20));
          }}
          onZoomOut={() => {
            if (!map) return;
            map.setZoom(Math.max((map.getZoom?.() || 0) - 1, 2));
          }}
          getVehicleIcon={getVehicleIcon}
          mobileMode={isMobile}
          bottomOffset={bottomPanelHeight || 0}
          getDirections={(lat, lng) => {
            window.open(
              `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
              "_blank",
            );
          }}
          handleCommand={() => {
            if (handleOpenCommandModal && activePanelDevice) {
              handleOpenCommandModal(activePanelDevice, {
                uiMode: "simple",
                commandScope: "engine_only",
              });
            }
          }}
          onStartTrip={() =>
            activePanelDevice &&
            (onStartTrip
              ? onStartTrip(activePanelDevice)
              : handleStartTripForDevice(activePanelDevice))
          }
          onEndTrip={() =>
            activePanelDevice &&
            (onEndTrip
              ? onEndTrip(activePanelDevice)
              : handleEndTripForDevice(activePanelDevice))
          }
          activeTrip={selectedDeviceTrip}
          onGetMyTrip={() => activePanelDevice && handleGetMyTrip(activePanelDevice)}
          onGetMyRoute={() => activePanelDevice && handleGetMyRoute(activePanelDevice)}
          onOpenCamera={() => activePanelDevice && setShowCameraPopup(true)}
          onShowHistory={() => activePanelDevice && handleShowInMapHistory(activePanelDevice)}
          vehicleStatus={getDeviceStatus(
            activePanelDevice,
            activePanelDevice ? socketUpdatesRef.current[activePanelDevice._id] : null,
          )}
          onCreateGeofence={() => activePanelDevice && openGeofenceModal(activePanelDevice)}
          onOpenReport={() => activePanelDevice && handleOpenReport(activePanelDevice)}
          onSaveBookmark={() => {
            if (map && activePanelDevice) {
              const name = `${activePanelDevice.name} - ${new Date().toLocaleString()}`;
              setBookmarks((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  name,
                  center: map.getCenter(),
                  zoom: map.getZoom(),
                },
              ]);
              showStatus("success", "Bookmark saved!");
            }
          }}
      />
      )}

      {showInMapHistory && historyDevice && (
        <InMapHistoryOverlay
          deviceId={historyDevice._id}
          deviceName={historyDevice.name}
          onClose={() => {
            setShowInMapHistory(false);
            setHistoryDevice(null);
          }}
          map={map}
          onPlayTrip={(tripData) => {
            setShowInMapHistory(false);
            setHistoryDevice(null);
            handleShowPlayback(tripData);
          }}
        />
      )}

      {showTripHistory && historyDevice && (
        <TripHistoryPanel
          deviceId={historyDevice._id}
          deviceName={historyDevice.name}
          onClose={closeTripHistory}
          map={map}
          onPlayTrip={(tripData) => {
            setShowTripHistory(false);
            handleShowPlayback(tripData);
          }}
        />
      )}

      {showGeofenceModal && selectedDeviceForGeofence && (
        <GeofenceModal
          selectedDeviceForGeofence={selectedDeviceForGeofence}
          geofenceName={geofenceName}
          setGeofenceName={setGeofenceName}
          unsavedGeofence={unsavedGeofence}
          setUnsavedGeofence={setUnsavedGeofence}
          onGeofenceCreated={onGeofenceCreated}
          onGeofenceEdited={onGeofenceEdited}
          handleSaveGeofence={handleSaveGeofence}
          handleDeleteGeofence={handleDeleteGeofence}
          setShowGeofenceModal={setShowGeofenceModal}
          successMessage={geofenceSuccessMessage}
          errorMessage={geofenceErrorMessage}
          getVehicleIcon={getVehicleIcon}
        />
      )}
      <SharedGeofenceModal
        open={showSharedGeofenceModal}
        mode={sharedGeofenceMode}
        initialShapeType={sharedGeofenceShapeType}
        geofence={editingSharedGeofence}
        onClose={() => {
          setShowSharedGeofenceModal(false);
          setEditingSharedGeofence(null);
        }}
        onSubmit={handleSaveSharedGeofence}
        saving={creatingSharedGeofence || updatingSharedGeofence || deletingSharedGeofence}
      />
      <GeofenceAssignModal
        open={showAssignGeofenceModal}
        geofence={activeAssigningGeofence}
        devices={assignableDevices}
        pendingDeviceIds={pendingGeofenceDeviceIds}
        onClose={() => {
          setShowAssignGeofenceModal(false);
          setAssigningGeofence(null);
        }}
        onToggleDevice={handleToggleSharedGeofenceDevice}
      />
      <LandmarkAssignModal
        open={showAssignLandmarkModal}
        landmark={activeAssigningLandmark}
        devices={assignableDevices}
        pendingDeviceIds={pendingLandmarkDeviceIds}
        onClose={() => {
          setShowAssignLandmarkModal(false);
          setAssigningLandmark(null);
        }}
        onToggleDevice={handleToggleLandmarkDevice}
      />

      {showAlerts && (
        <AlertsPanel
          alerts={deviceEvents}
          onClose={() => setShowAlerts(false)}
          onDismiss={onDismissAlert}
        />
      )}
      {showPlayback && playbackData && (
        <TripPlayback
          tripData={playbackData}
          onClose={closeReplayPlayback}
          map={map}
          onPanelHeightChange={setBottomPanelHeight}
          sidebarOffset={!isViewer && sidebarOpen ? 416 : 0}
          rightOffset={0}
          getVehicleIcon={getVehicleIcon}
          onPlaybackFrame={setPlaybackFrameData}
        />
      )}
      {showHistory && historyDevice && (
        <HistoryPlayback
          deviceId={historyDevice._id}
          deviceName={historyDevice.name}
          onClose={closeHistoryPlayback}
          map={map}
          getVehicleIcon={getVehicleIcon}
          vehicleType={historyDevice.vehicleType}
        />
      )}
      <RegisterDeviceModal
        open={showRegisterDeviceModal}
        onClose={handleCloseRegisterDevice}
        mode={editingDevice ? "edit" : "create"}
        initialDevice={editingDevice}
      />
      <MapCameraPopup
        open={showCameraPopup && Boolean(activePanelDevice)}
        device={activePanelDevice}
        onClose={() => setShowCameraPopup(false)}
      />

      {/* Modals */}
      {showTripModal && (
        <AddTripModal
          onClose={() => setShowTripModal(false)}
          onSubmit={handleTripSubmit}
        />
      )}
      {showRouteModal && (
        <AddRouteModal
          onClose={() => setShowRouteModal(false)}
          onSubmit={handleRouteSubmit}
        />
      )}
      {showExpenseModal && (
        <AddExpenseModal
          onClose={() => setShowExpenseModal(false)}
          onSubmit={handleExpenseSubmit}
        />
      )}
      {showDriverModal && (
        <AddDriverModal
          onClose={() => setShowDriverModal(false)}
          onSubmit={handleDriverSubmit}
        />
      )}
      {showClientModal && (
        <AddClientModal
          onClose={() => setShowClientModal(false)}
          onSubmit={handleClientSubmit}
        />
      )}
      {showReminderModal && (
        <AddReminderModal
          onClose={() => setShowReminderModal(false)}
          onSubmit={handleReminderSubmit}
        />
      )}
      {showMaterialModal && (
        <AddMaterialModal
          onClose={() => setShowMaterialModal(false)}
          onSubmit={handleMaterialSubmit}
        />
      )}
      {showLicenseModal && (
        <AddLicenseModal
          onClose={() => setShowLicenseModal(false)}
          onSubmit={handleLicenseSubmit}
        />
      )}
      {showDocumentModal && (
        <AddDocumentModal
          onClose={() => setShowDocumentModal(false)}
          onSubmit={handleDocumentSubmit}
        />
      )}
      {showSensorModal && (
        <AddSensorModal
          onClose={() => setShowSensorModal(false)}
          onSubmit={handleSensorSubmit}
        />
      )}
      {showReportModal && (
        <ReportsPortal
          isOpen={showReportModal}
          allDevices={sidebarSourceDevices}
          onClose={() => {
            setShowReportModal(false);
            setSelectedDeviceData(null);
          }}
          rawData={selectedDeviceData}
          onReplayTrip={handleReplayTripFromReport}
        />
      )}
    </div>
  );
}

export default AdvancedMapTracker;
